import React, { useEffect, useState, useCallback, useRef } from 'react';
import axiosInstance from '../utility/axiosInstance';
import { useToast } from '../components/ToastContext';
import { handleAxiosError } from '../helpers/handleAxiosError';
import { FaSyncAlt, FaRegCopy, FaCheck, FaTimes, FaPlus } from 'react-icons/fa';
import DeleteHeldBadge, { DeleteHold } from '../components/DeleteHeldBadge';
import '../styles/AdminPage.scss';

type CardFlag = { kind: 'shared_card' | 'card_batch'; peers: number; internal_peers: number };

/** billing_customers.returning_from (ROADMAP 105): this account matches a deleted account's email or card marker. */
type ReturningMatch = { old_user_id: string; reason: 'unpaid' | 'abuse'; matched_on: 'email' | 'card'; matched_at: string };
type ReturningFrom = ReturningMatch & { matches?: ReturningMatch[] };

interface UserBillingRow {
    user_id: string;
    username: string;
    card_flag?: CardFlag | null;
    card_seen_at?: string | null;
    os_project: string;
    os_id: string;
    month_cost: number;
    month_cost_basis?: 'subscription_fee_plus_net_paid_usage';
    subscription_period_cost?: number | null;
    subscription_period?: { start: string; end: string } | null;
    subscription_period_status?: 'available' | 'no_subscription' | 'unavailable';
    subscription_period_basis?: 'subscription_fee_plus_net_paid_usage';
    active_instances: number;
    active_volumes: number;
    active_snapshots: number;
    active_backups: number;
    active_networks: number;
    credits_remaining: number;
    is_internal?: boolean;
    suspended_by_admin?: boolean;
    // false when the account has no Stripe subscription, i.e. nothing can ever be collected from it.
    chargeable?: boolean;
    billing_status?: string | null;
    tier?: string | null;
    stripe_customer_id?: string | null;
    credits_expire_at?: string | null;
    payment_failed_at?: string | null;
    next_bill_at?: string | null;
    burn_rate_hourly?: number;
    projection?: { accrued: number; burn: number; hoursLeft: number; total: number; afterCredit: number; basis?: string };
    state?: string;
    plan_label?: string;
    plan_label_inputs?: PlanLabelInputs | null;
}

/**
 * One derived state per account, so the table answers "who is actually alive here?" at a glance.
 *
 * ⛔ THE ORDER OF THESE TESTS IS THE MEANING. Suspended beats everything, because a suspended
 * account that still holds resources must not read as "active". Chargeability comes before
 * activity, because a no-card trial running three instances is not a customer.
 */
type AccountState = 'suspended' | 'failed' | 'paying' | 'on_credit' | 'inactive' | 'trial' | 'expired';

/**
 * Two accounts hold the same card, or cards from one issued batch.
 *
 * ⛔ IT IS A FLAG AND NOT A VERDICT, and the wording has to carry that. Two of the four fingerprint
 * collisions on this platform are the founders' own test accounts, so a pill that reads like an
 * accusation would be wrong half the time on the data that produced it. It sits INSIDE the user
 * cell rather than as a column: a ninth column pushed Actions off the right edge once already.
 */
const CardPill: React.FC<{ flag?: CardFlag | null }> = ({ flag }) => {
    if (!flag) return null;
    const shared = flag.kind === 'shared_card';
    const label = shared ? `same card ×${flag.peers + 1}` : `card batch ×${flag.peers + 1}`;
    // ⛔ SAY WHEN PEERS WERE EXCLUDED. Silently dropping internal accounts makes a suppressed ring
    // render exactly like no ring, and one wrong click on "Mark internal" is all it takes.
    const suffix = flag.internal_peers > 0 ? ` (+${flag.internal_peers} internal, not counted)` : '';
    return (
        <span
            title={shared
                ? `Stripe reports the SAME physical card on ${flag.peers + 1} accounts.${suffix} Strong signal, but not proof: a shared card can be a family or a company. Internal (redu) accounts are never counted towards the total.`
                : `${flag.peers + 1} accounts hold different cards issued in one batch (same brand, funding, country and expiry, minted within 30 days).${suffix} Weak signal on its own: check the accounts before acting.`}
            style={{
                marginLeft: 8, padding: '1px 7px', borderRadius: 10, fontSize: '0.68rem', fontWeight: 600,
                whiteSpace: 'nowrap', cursor: 'help',
                background: shared ? '#fee2e2' : '#fef3c7',
                color: shared ? '#991b1b' : '#92400e',
                border: `1px solid ${shared ? '#fecaca' : '#fde68a'}`,
            }}
        >
            {label}{suffix ? ' +' : ''}
        </span>
    );
};

/**
 * A deleted unpaid or abuse account seems to be back: this account's email (normalized, compared as a keyed hash) or one
 * of its cards matches that account's retention marker. ⛔ A FLAG, NOT A VERDICT, and nothing was blocked: a shared
 * family card or a recycled address can match too. Check the old account before acting.
 */
const ReturningPill: React.FC<{ from?: ReturningFrom | null }> = ({ from }) => {
    if (!from) return null;
    const matches = from.matches && from.matches.length ? from.matches : [from];
    const abuse = matches.some((m) => m.reason === 'abuse');
    const lines = matches.map((m) => `${m.matched_on} matches ${shortId(m.old_user_id)} (deleted ${m.reason}, ${new Date(m.matched_at).toLocaleDateString()})`);
    return (
        <span
            title={`Returning account? ${lines.join('; ')}. A flag only: nothing was blocked. Check the old account before acting.`}
            style={{
                marginLeft: 6, padding: '1px 7px', borderRadius: 10, fontSize: '0.68rem', fontWeight: 600,
                whiteSpace: 'nowrap', cursor: 'help',
                background: abuse ? '#fee2e2' : '#fef3c7',
                color: abuse ? '#991b1b' : '#92400e',
                border: `1px solid ${abuse ? '#fecaca' : '#fde68a'}`,
            }}
        >
            {abuse ? 'returning: abuse' : 'returning: unpaid'}
        </span>
    );
};

const ACCOUNT_STATE: Record<AccountState, { label: string; colour: string; hint: string }> = {
    paying:    { label: 'Paid user', colour: '#22c55e', hint: 'Has a card, running something, credit spent: this is being invoiced' },
    on_credit: { label: 'On credit', colour: '#3b82f6', hint: 'Has a card and is running something, but still spending welcome credit, so not paying yet' },
    inactive:  { label: 'Inactive',  colour: '#64748b', hint: 'Has a card but is running nothing. A card is not usage.' },
    trial:     { label: 'Trial',     colour: '#8b5cf6', hint: 'No card, still inside its credit' },
    expired:   { label: 'Expired',   colour: '#7f1d1d', hint: 'No card and the credit is gone: nothing here can ever be collected' },
    failed:    { label: 'Payment failed', colour: '#f59e0b', hint: 'Stripe reported a failed invoice (failed_at is set)' },
    suspended: { label: 'Suspended', colour: '#ef4444', hint: 'Suspended BY AN ADMIN: machines stopped, public URLs offline, and blocked from creating or restarting anything' },
};

/**
 * ⛔ THE STATE IS COMPUTED ON THE SERVER (adminController.accountState) and read here.
 *
 * It used to be derived in both places from the same idea independently, and they disagreed: the
 * funnel counted an account as "paying" while the pill showed something else. One definition means
 * the pill, the filter and every count cannot drift apart.
 */
function accountState(u: UserBillingRow): AccountState {
    return (u.state as AccountState) ?? 'inactive';
}

/**
 * The plan each account is on, computed on the server (adminPlanLabel.ts) like the state. Milos, 2026-11-02: "properly
 * label users now as: regular. free tier (if they are using just free tier) and so on". It sits BESIDE the state pill,
 * outlined where the state is filled: the plan says what the account is on, the state how its billing is doing.
 */
type PlanLabel = 'enterprise' | 'professional' | 'plan_unpaid' | 'regular' | 'free_tier' | 'legacy' | 'no_plan';
type PlanLabelInputs = {
    plan: string | null; activated: boolean; old_billing: boolean; card: boolean;
    paid_running: number; free_vms: number; free_postgres: number;
};

const PLAN_LABEL: Record<PlanLabel, { label: string; colour: string; hint: string }> = {
    enterprise:   { label: 'Enterprise',   colour: '#0d9488', hint: 'Enterprise plan, first payment received' },
    professional: { label: 'Professional', colour: '#2563eb', hint: 'Professional plan, first payment received' },
    plan_unpaid:  { label: 'Plan unpaid',  colour: '#d97706', hint: 'Chose Professional or Enterprise, but the first payment has not gone through' },
    regular:      { label: 'Regular',      colour: '#16a34a', hint: 'Regular plan, running something paid' },
    free_tier:    { label: 'Free tier',    colour: '#7c3aed', hint: 'Regular plan, nothing paid running: only the free tier, or nothing yet' },
    // Milos, 2026-11-02: "Call it still regular but with just a (l) sign". (l) = legacy.
    legacy:       { label: 'Regular (l)',  colour: '#16a34a', hint: 'Regular, legacy: joined before plans existed, still on the old billing' },
    no_plan:      { label: 'No plan',      colour: '#78716c', hint: 'Never chose a plan or set up billing' },
};

/**
 * What the account runs, for the tooltip. Paid means seen by the billing recorder in the last 3 hours; free means a free
 * machine that is running, not a queued request. The card is the server's card fact, not `chargeable`: a Plan unpaid
 * account whose saved card was declined has a card but cannot be charged yet.
 */
function planDetail(i: PlanLabelInputs | null | undefined): string {
    if (!i) return '';
    const free = [i.free_vms > 0 && 'free VM', i.free_postgres > 0 && 'free Postgres'].filter(Boolean).join(' + ');
    return `Free tier: ${free || 'nothing'}. Paid resources running: ${Number(i.paid_running) || 0}. ${i.card ? 'Card added' : 'No card added'}.`;
}

const PlanPill: React.FC<{ label?: string; inputs?: PlanLabelInputs | null }> = ({ label, inputs }) => {
    const meta = PLAN_LABEL[label as PlanLabel];
    if (!meta) return null;
    return (
        <span title={`${meta.hint}. ${planDetail(inputs)}`.trim()}
              style={{ marginRight: 6, border: `1px solid ${meta.colour}`, color: meta.colour, borderRadius: 999, padding: '1px 9px', fontSize: '0.72rem', fontWeight: 700, whiteSpace: 'nowrap' }}>
            {meta.label}
        </span>
    );
};

/**
 * What of the free tier the account runs right now, readable without hovering. Milos, 2026-11-03: "V (for VM) B (for
 * database) and VB for both". Same counts as the tooltip: machines in state 'active', never a queued request.
 */
const FreeTierMark: React.FC<{ inputs?: PlanLabelInputs | null }> = ({ inputs }) => {
    const vms = Number(inputs?.free_vms) || 0;
    const dbs = Number(inputs?.free_postgres) || 0;
    if (!vms && !dbs) return null;
    const mark = `${vms ? 'V' : ''}${dbs ? 'B' : ''}`;
    const colour = PLAN_LABEL.free_tier.colour;
    return (
        <span aria-label={`Free tier running: ${mark}`}
              title={`Free tier running now: ${vms} VM${vms === 1 ? '' : 's'}, ${dbs} database${dbs === 1 ? '' : 's'}`}
              style={{ marginRight: 6, background: `${colour}22`, color: colour, borderRadius: 4, padding: '1px 5px', fontSize: '0.7rem', fontWeight: 800, fontFamily: 'monospace', letterSpacing: '0.04em' }}>
            {mark}
        </span>
    );
};

/** Days until the soonest still-funded credit grant expires, or null when there is none. */
function creditDaysLeft(iso: string | null | undefined): number | null {
    if (!iso) return null;
    return Math.ceil((new Date(iso).getTime() - Date.now()) / 86400000);
}

interface Summary {
    total_users: number;
    total_month_revenue: number;            // usage NOT covered by credit (an estimate, see revenue_basis)
    total_month_gross_usage?: number;       // list-price usage before credit
    total_month_credit_covered?: number;    // the part credit is absorbing
    total_month_trial_usage?: number;       // usage by accounts that CANNOT be charged
    trial_users?: number;
    total_projected_billable?: number;
    funnel?: {
        accounts: number; paying: number; on_credit: number; inactive: number; trial: number;
        expired: number; payment_failed: number; suspended: number;
        carded: number; no_card: number; running: number; internal: number;
    };
    revenue_basis?: string;
    internal_users?: number;
    internal_month_gross_usage?: number;
    total_active_instances: number;
    total_active_volumes: number;
    total_free_vms?: number;
    total_free_postgres?: number;
}

interface CreditRow {
    id: string | number;
    amount: number;
    used: number;
    remaining: number;
    type: string;
    expires_at: string;
    granted_at: string;
}

interface AdminCreditRow {
    id: string | number;
    user_id: string;
    amount: number;
    used: number;
    remaining: number;
    type: string;
    expires_at: string;
    granted_at: string;
    os_id: string | null;
    is_expired: boolean;
    stripe_credit_grant_id?: string | null;
    credit_source?: 'stripe' | 'legacy_local' | 'account_ledger';
    stripe_customer_id?: string | null;
}

const CopyButton: React.FC<{ text: string }> = ({ text }) => {
    const [copied, setCopied] = useState(false);
    const handle = () => {
        navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
    };
    return (
        <button onClick={handle} title="Copy" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: '0 4px' }}>
            {copied ? <FaCheck size={11} color="#22c55e" /> : <FaRegCopy size={11} />}
        </button>
    );
};

const fmt = (n: number) => `£${n.toFixed(4)}`;
const shortId = (id: string) => id ? `${id.slice(0, 8)}…` : '-';

// ─── Credit management panel ───────────────────────────────────────────────

const CreditsPanel: React.FC<{ userId: string; stripeCustomerId: string | null; onClose: () => void }> = ({ userId, stripeCustomerId, onClose }) => {
    const { showToast } = useToast();
    const [credits, setCredits] = useState<CreditRow[]>([]);
    const [loading, setLoading] = useState(false);
    const [amount, setAmount] = useState('');
    const [expiresAt, setExpiresAt] = useState('');
    const [granting, setGranting] = useState(false);
    const grantRequest = useRef<{body:string;key:string}|null>(null);
    const [localCreditLedger,setLocalCreditLedger]=useState(false);
    const canGrantStripeCredit = Boolean(stripeCustomerId) || localCreditLedger;

    const fetchCredits = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axiosInstance.get(`/v1/admin/credits?user_id=${userId}`);
            setCredits(res.data);
            setLocalCreditLedger(res.headers['x-redu-credit-source']==='account_ledger');
        } catch {
            showToast('Failed to load credits', 'error');
        } finally {
            setLoading(false);
        }
    }, [userId, showToast]);

    useEffect(() => { fetchCredits(); }, [fetchCredits]);

    const handleGrant = async () => {
        if (!canGrantStripeCredit || !amount || !expiresAt) return;
        setGranting(true);
        const body={user_id:userId,amount:parseFloat(amount),expires_at:expiresAt};
        const fingerprint=JSON.stringify(body);
        if(grantRequest.current?.body!==fingerprint)grantRequest.current={body:fingerprint,key:crypto.randomUUID()};
        try {
            await axiosInstance.post('/v1/admin/credits', body, {headers:{'Idempotency-Key':grantRequest.current!.key}});
            grantRequest.current=null;
            showToast('Credit granted', 'success');
            setAmount('');
            setExpiresAt('');
            fetchCredits();
        } catch {
            showToast('Failed to grant credit', 'error');
        } finally {
            setGranting(false);
        }
    };

    const handleRevoke = async (id: string | number) => {
        try {
            await axiosInstance.delete(`/v1/admin/credits/${id}`);
            showToast('Credit revoked', 'success');
            fetchCredits();
        } catch {
            showToast('Failed to revoke credit', 'error');
        }
    };

    return (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
            <div style={{ background: 'var(--bg-card)', borderRadius: '12px', width: '640px', maxWidth: '95vw', maxHeight: '85vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 24px', borderBottom: '1px solid var(--border)' }}>
                    <div>
                        <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 700 }}>Credit Management</h3>
                        <p style={{ margin: '2px 0 0', fontSize: '0.75rem', color: 'var(--text-muted)', fontFamily: 'monospace' }}>{userId}</p>
                    </div>
                    <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: '1.2rem' }}>
                        <FaTimes />
                    </button>
                </div>

                <div style={{ padding: '20px 24px', borderBottom: '1px solid var(--border)' }}>
                    <p style={{ margin: '0 0 4px', fontWeight: 600, fontSize: '0.875rem' }}>Grant paid-usage credit</p>
                    {!canGrantStripeCredit && (
                        <p role="status" style={{ margin: '0 0 12px', fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                            Disabled: this no-card trial has no Stripe billing profile.
                        </p>
                    )}
                    <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                        <input
                            type="number"
                            min="0.01"
                            step="0.01"
                            placeholder="Amount (£)"
                            value={amount}
                            onChange={e => setAmount(e.target.value)}
                            disabled={!canGrantStripeCredit}
                            style={{ flex: '1', minWidth: '120px', padding: '8px 12px', border: '1px solid var(--border)', borderRadius: '6px', fontSize: '0.875rem' }}
                        />
                        <input
                            type="date"
                            value={expiresAt}
                            onChange={e => setExpiresAt(e.target.value)}
                            disabled={!canGrantStripeCredit}
                            style={{ flex: '1', minWidth: '140px', padding: '8px 12px', border: '1px solid var(--border)', borderRadius: '6px', fontSize: '0.875rem' }}
                        />
                        <button
                            onClick={handleGrant}
                            disabled={!canGrantStripeCredit || granting || !amount || !expiresAt}
                            style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 16px', background: 'var(--action-primary-bg)', color: 'var(--action-primary-text)', border: 'none', borderRadius: '6px', cursor: (!canGrantStripeCredit || granting || !amount || !expiresAt) ? 'not-allowed' : 'pointer', opacity: (!canGrantStripeCredit || granting || !amount || !expiresAt) ? 0.7 : 1, fontSize: '0.875rem', fontWeight: 500, whiteSpace: 'nowrap' }}
                        >
                            <FaPlus size={11} /> {granting ? 'Granting…' : 'Grant'}
                        </button>
                    </div>
                </div>

                <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>
                    {loading ? (
                        <p style={{ color: 'var(--text-muted)', textAlign: 'center' }}>Loading…</p>
                    ) : credits.length === 0 ? (
                        <p style={{ color: 'var(--text-muted)', textAlign: 'center' }}>No credits for this user.</p>
                    ) : (
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                            <thead>
                                <tr style={{ borderBottom: '1px solid var(--border)', color: 'var(--text-muted)' }}>
                                    {['Granted', 'Amount', 'Used', 'Remaining', 'Expires', ''].map(h => (
                                        <th key={h} style={{ padding: '6px 8px', textAlign: 'left', fontWeight: 600 }}>{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {credits.map(c => {
                                    const expired = new Date(c.expires_at) < new Date();
                                    return (
                                        <tr key={c.id} style={{ borderBottom: '1px solid var(--border)', opacity: expired ? 0.5 : 1 }}>
                                            <td style={{ padding: '8px', whiteSpace: 'nowrap' }}>{new Date(c.granted_at).toLocaleDateString()}</td>
                                            <td style={{ padding: '8px' }}>£{parseFloat(String(c.amount)).toFixed(2)}</td>
                                            <td style={{ padding: '8px' }}>£{parseFloat(String(c.used)).toFixed(4)}</td>
                                            <td style={{ padding: '8px', color: parseFloat(String(c.remaining)) > 0 ? '#15803d' : '#6b7280', fontWeight: 600 }}>
                                                £{parseFloat(String(c.remaining)).toFixed(4)}
                                            </td>
                                            <td style={{ padding: '8px', whiteSpace: 'nowrap', color: expired ? '#ef4444' : '#374151' }}>
                                                {new Date(c.expires_at).toLocaleDateString()}
                                                {expired && <span style={{ marginLeft: '4px', fontSize: '0.7rem' }}>(expired)</span>}
                                            </td>
                                            <td style={{ padding: '8px' }}>
                                                {!expired && (
                                                    <button
                                                        onClick={() => handleRevoke(c.id)}
                                                        title="Revoke"
                                                        style={{ background: 'none', border: '1px solid #fca5a5', borderRadius: '4px', color: '#ef4444', cursor: 'pointer', padding: '3px 8px', fontSize: '0.75rem' }}
                                                    >
                                                        Revoke
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        </div>
    );
};

// ─── Provisioning status tab ───────────────────────────────────────────────

interface ProvisioningRow {
    user_id: string;
    username: string;
    card_flag?: CardFlag | null;
    returning_from?: ReturningFrom | null;
    card_seen_at?: string | null;
    os_id: string;
    os_project: string;
    billing_status: string | null;
    credits_remaining: number;
    active_instances: number;
    active_volumes: number;
}

const STATUS_COLOR: Record<string, string> = {
    active: '#22c55e',
    past_due: '#f59e0b',
    for_deletion: '#ef4444',
    canceled: '#6b7280',
    trial: 'var(--status-info)',
};


/**
 * The per-account admin actions, shared by the Billing table and the Users table.
 *
 * ⛔ ONE COPY ON PURPOSE. These were originally only on the Users tab, which the Billing tab's
 * "Manage" button jumped to without even selecting the account, so from the tab Milos actually uses
 * they were unreachable. Two copies of "suspend this account" is how one of them quietly stops
 * matching the other.
 */
const AccountActions: React.FC<{
    userId: string;
    username: string;
    isInternal: boolean;
    suspendedByAdmin: boolean;
    onDone: () => void | Promise<void>;
    onCredits?: () => void;
}> = ({ userId, username, isInternal, suspendedByAdmin, onDone, onCredits }) => {
    const { showToast } = useToast();
    const [busy, setBusy] = useState(false);
    const [open, setOpen] = useState(false);
    const anchorRef = useRef<HTMLButtonElement | null>(null);
    const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

    // Positioned against the viewport rather than the row: the table scrolls horizontally
    // (overflowX: auto), and a menu positioned inside it gets clipped at the edge.
    const toggle = () => {
        if (open) { setOpen(false); return; }
        const r = anchorRef.current?.getBoundingClientRect();
        if (r) {
            // ⛔ FLIP UP NEAR THE BOTTOM. The menu is positioned against the viewport, so for a row
            // near the bottom of the window it was drawn below the fold at a fixed position and
            // could not be scrolled to. That is the third time a control here existed and could not
            // be reached.
            const MENU_H = 170;
            const below = r.bottom + 4;
            const top = below + MENU_H > window.innerHeight ? Math.max(8, r.top - MENU_H - 4) : below;
            setPos({ top, left: Math.max(8, Math.min(r.right - 190, window.innerWidth - 200)) });
        }
        setOpen(true);
    };

    const run = async (fn: () => Promise<any>, ok: string) => {
        setOpen(false);
        setBusy(true);
        try {
            await fn();
            showToast(ok, 'success');
            await onDone();
        } catch (e: any) {
            showToast(handleAxiosError(e) || 'Action failed', 'error');
        } finally {
            setBusy(false);
        }
    };

    const suspend = () => {
        setOpen(false);
        // A suspension with no recorded reason is one nobody dares lift later, so the API requires
        // one and it is asked for here rather than defaulted.
        const reason = window.prompt(`Suspend ${username}?\n\nStops every running machine, takes their public URLs offline, and blocks every create and every restart until you lift it.\n\nReason (recorded in the audit log):`);
        if (!reason || reason.trim().length < 3) return;
        return run(() => axiosInstance.post(`/v1/admin/users/${userId}/suspend`, { reason: reason.trim() }), 'Account suspended');
    };

    const remove = async () => {
        setOpen(false);
        setBusy(true);
        try {
            // Dry run FIRST, always: the operator sees exactly what would go before anything does.
            // This is DRYRUN=1 from the manual ops procedure.
            const preview = await axiosInstance.delete(`/v1/admin/users/${userId}`, { data: { confirm: username, dry_run: true } });
            const w = preview.data.would_delete ?? {};
            const resources = (w.active_resources ?? []).map((r: any) => `${r.active} ${r.resource_type}`).join(', ') || 'no active resources';
            const typed = window.prompt(
                `DELETE ${username}\n\nIrreversible. This removes:\n` +
                `  - ${resources}\n` +
                `  - OpenStack project ${String(w.openstack_project ?? '').slice(0, 8)}... and its user\n` +
                `  - ${(w.proxy_hosts ?? []).length} proxy host(s)\n` +
                `  - ${(w.stripe_credit_grants ?? []).length} credit grant(s), voided\n` +
                `  - every app database row and the Keycloak user\n\n` +
                // An admin delete is the abuse path: the card is refused on any future account and the
                // Stripe customer stays as the record. Unbilled usage is written off, never charged.
                `As abuse:\n` +
                `  - BLOCKS ${(w.cards_to_block ?? []).length} card(s) on any future account (plus any card on its Stripe customer)` +
                `${(w.cards_not_blocked_internal ?? []).length ? `; ${(w.cards_not_blocked_internal ?? []).length} card(s) not blocked, a founder account holds them` : ''}\n` +
                `  - KEEPS Stripe customer ${w.stripe_customer ?? '(none)'} marked as abuse; subscriptions cancelled, nothing charged\n\n` +
                // ROADMAP 63 (Q1, Q2): a name that cannot be removed holds the whole delete.
                `If one of its public names cannot be removed, the delete holds before anything irreversible (no address released, nothing in billing changes, its machines powered off) and the account shows "delete held".\n\n` +
                `Type the account's email to confirm:`
            );
            if (!typed) { setBusy(false); return; }
            const res = await axiosInstance.delete(`/v1/admin/users/${userId}`, { data: { confirm: typed.trim() } });
            showToast(`Teardown started (${res.data.workflow_id})`, 'success');
            await onDone();
        } catch (e: any) {
            showToast(handleAxiosError(e) || 'Delete failed', 'error');
        } finally {
            setBusy(false);
        }
    };

    const item = (label: string, onClick: () => void, colour?: string, title?: string) => (
        <button
            key={label}
            onClick={onClick}
            title={title}
            style={{
                display: 'block', width: '100%', textAlign: 'left', padding: '7px 12px',
                background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.8rem',
                color: colour ?? 'var(--text-primary)', whiteSpace: 'nowrap',
            }}
            onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = 'var(--surface-alt)'; }}
            onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = 'none'; }}
        >
            {label}
        </button>
    );

    return (
        <>
            <button
                ref={anchorRef}
                onClick={toggle}
                disabled={busy}
                aria-label={`Actions for ${username}`}
                style={{
                    padding: '4px 10px', background: 'none', border: '1px solid var(--border-strong)',
                    color: 'var(--text-primary)', borderRadius: '6px', cursor: busy ? 'not-allowed' : 'pointer',
                    fontSize: '0.8rem', fontWeight: 500, opacity: busy ? 0.5 : 1, whiteSpace: 'nowrap',
                }}
            >
                {busy ? '…' : 'Actions ▾'}
            </button>
            {open && pos && (
                <>
                    {/* Full-screen catcher so a click anywhere closes the menu. */}
                    <div onClick={() => setOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 900 }} />
                    <div style={{
                        position: 'fixed', top: pos.top, left: pos.left, minWidth: 190, zIndex: 901,
                        background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 8,
                        boxShadow: '0 8px 24px rgba(0,0,0,0.28)', padding: '4px 0', overflow: 'hidden',
                    }}>
                        {onCredits && item('Credits', () => { setOpen(false); onCredits(); })}
                        {item(
                            isInternal ? 'Count as customer' : 'Mark internal',
                            () => run(() => axiosInstance.post(`/v1/admin/users/${userId}/internal`, { internal: !isInternal }), isInternal ? 'Counted as a customer again' : 'Marked internal'),
                            'var(--text-secondary)',
                            isInternal ? 'Count this account in the totals again' : "redu's own account: hidden from these lists and excluded from every total",
                        )}
                        <div style={{ height: 1, background: 'var(--border)', margin: '4px 0' }} />
                        {suspendedByAdmin
                            ? item('Unsuspend', () => run(() => axiosInstance.post(`/v1/admin/users/${userId}/unsuspend`, {}), 'Suspension lifted'), '#22c55e',
                                   'Restore the previous status, re-enable exactly the proxy hosts the suspension disabled, and restart exactly the machines it stopped')
                            : item('Suspend', suspend, '#f59e0b',
                                   'Stop their running machines, take their public URLs offline, and block every create and restart.')}
                        {item('Delete account…', remove, '#ef4444', 'Irreversible. Shows exactly what would be deleted first.')}
                    </div>
                </>
            )}
        </>
    );
};

const ProvisioningTab: React.FC = () => {
    const { showToast } = useToast();
    const [rows, setRows] = useState<ProvisioningRow[]>([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(0);
    const [filterStatus, setFilterStatus] = useState('');
    const limit = 25;

    const fetchRows = useCallback(async (pg = 0, status = filterStatus) => {
        setLoading(true);
        try {
            const params = new URLSearchParams({ limit: String(limit), offset: String(pg * limit) });
            if (status) params.set('billing_status', status);
            const res = await axiosInstance.get(`/v1/admin/users?${params}`);
            setRows(res.data.users);
            setTotal(res.data.total);
        } catch {
            showToast('Failed to load users', 'error');
        } finally {
            setLoading(false);
        }
    }, [showToast, filterStatus]);

    useEffect(() => { fetchRows(); }, [fetchRows]);

    const handleReset = async (userId: string) => {
        try {
            await axiosInstance.post(`/v1/admin/users/${userId}/reset-billing`);
            showToast('Billing status reset to active', 'success');
            fetchRows(page);
        } catch {
            showToast('Failed to reset billing status', 'error');
        }
    };

    const statusOptions = ['', 'active', 'past_due', 'for_deletion', 'canceled', 'trial'];

    return (
        <div style={{ background: 'var(--surface-card)', border: '1px solid var(--border)', borderRadius: '10px', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid var(--border)', flexWrap: 'wrap', gap: '10px' }}>
                <span style={{ fontWeight: 600, fontSize: '0.95rem', color: 'var(--text-primary)' }}>Provisioned Users ({total})</span>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                    <select
                        value={filterStatus}
                        onChange={e => { setFilterStatus(e.target.value); setPage(0); fetchRows(0, e.target.value); }}
                        style={{ padding: '5px 10px', background: 'var(--surface-input)', color: 'var(--text-primary)', border: '1px solid var(--border)', borderRadius: '6px', fontSize: '0.8rem' }}
                    >
                        {statusOptions.map(s => <option key={s} value={s}>{s || 'All statuses'}</option>)}
                    </select>
                    <button onClick={() => fetchRows(page)} disabled={loading} style={{ background: 'none', border: '1px solid var(--border)', borderRadius: '6px', padding: '5px 12px', cursor: 'pointer', fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                        <FaSyncAlt size={11} style={{ marginRight: '4px' }} /> Refresh
                    </button>
                </div>
            </div>
            <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                    <thead>
                        <tr style={{ background: 'var(--surface-alt)', borderBottom: '1px solid var(--border)' }}>
                            {['User', 'OS User ID', 'OS Project', 'Billing Status', 'Credits', 'Instances', 'Actions'].map(h => (
                                <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 600, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>{h}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {loading && rows.length === 0 && <tr><td colSpan={7} style={{ padding: '32px', textAlign: 'center', color: 'var(--text-muted)' }}>Loading…</td></tr>}
                        {!loading && rows.length === 0 && <tr><td colSpan={7} style={{ padding: '32px', textAlign: 'center', color: 'var(--text-muted)' }}>No users found.</td></tr>}
                        {rows.map(r => {
                            const badBilling = r.billing_status === 'past_due' || r.billing_status === 'for_deletion' || r.billing_status === 'canceled';
                            return (
                                <tr key={r.user_id} style={{ borderBottom: '1px solid var(--border)' }}>
                                    <td style={{ padding: '12px 16px', fontWeight: 500, color: 'var(--text-primary)' }}>
                                        {r.username}<CopyButton text={r.user_id} /><CardPill flag={r.card_flag} /><ReturningPill from={r.returning_from} />
                                    </td>
                                    <td style={{ padding: '12px 16px', fontFamily: 'monospace', fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                                        <span title={r.os_id}>{shortId(r.os_id)}</span><CopyButton text={r.os_id} />
                                    </td>
                                    <td style={{ padding: '12px 16px', fontFamily: 'monospace', fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                                        <span title={r.os_project}>{shortId(r.os_project)}</span><CopyButton text={r.os_project} />
                                    </td>
                                    <td style={{ padding: '12px 16px' }}>
                                        {r.billing_status ? (
                                            <span style={{ background: `${STATUS_COLOR[r.billing_status] || '#6b7280'}22`, color: STATUS_COLOR[r.billing_status] || '#6b7280', borderRadius: '999px', padding: '3px 10px', fontSize: '0.8rem', fontWeight: 600 }}>
                                                {r.billing_status}
                                            </span>
                                        ) : <span style={{ color: 'var(--text-muted)', fontSize: '0.8rem' }}>no record</span>}
                                    </td>
                                    <td style={{ padding: '12px 16px', fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                                        £{r.credits_remaining.toFixed(2)}
                                    </td>
                                    <td style={{ padding: '12px 16px', fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                                        {r.active_instances}
                                    </td>
                                    <td style={{ padding: '12px 16px' }}>
                                        {badBilling && (
                                            <button onClick={() => handleReset(r.user_id)} style={{ padding: '4px 12px', background: 'none', border: '1px solid #22c55e', color: '#15803d', borderRadius: '6px', cursor: 'pointer', fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                                                Reset billing
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
            {total > limit && (
                <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', padding: '12px', borderTop: '1px solid var(--border)' }}>
                    <button onClick={() => { const p = Math.max(0, page - 1); setPage(p); fetchRows(p); }} disabled={page === 0} style={{ padding: '5px 14px', background: 'none', border: '1px solid var(--border)', borderRadius: '6px', cursor: page === 0 ? 'not-allowed' : 'pointer', opacity: page === 0 ? 0.4 : 1, color: 'var(--text-primary)' }}>← Prev</button>
                    <span style={{ lineHeight: '30px', color: 'var(--text-muted)', fontSize: '0.8rem' }}>{page * limit + 1}-{Math.min((page + 1) * limit, total)} of {total}</span>
                    <button onClick={() => { const p = page + 1; setPage(p); fetchRows(p); }} disabled={(page + 1) * limit >= total} style={{ padding: '5px 14px', background: 'none', border: '1px solid var(--border)', borderRadius: '6px', cursor: (page + 1) * limit >= total ? 'not-allowed' : 'pointer', opacity: (page + 1) * limit >= total ? 0.4 : 1, color: 'var(--text-primary)' }}>Next →</button>
                </div>
            )}
        </div>
    );
};

// ─── Audit log tab ────────────────────────────────────────────────────────────

interface AuditEvent {
    id: number;
    user_id: string;
    username: string | null;
    action: string;
    resource_type: string | null;
    resource_id: string | null;
    ip_address: string | null;
    created_at: string;
}

const ACTION_COLOR: Record<string, string> = {
    'instance.create': '#22c55e',
    'instance.delete': '#ef4444',
    'volume.create':   'var(--status-info)',
    'volume.delete':   '#f97316',
    'snapshot.create': '#8b5cf6',
    'snapshot.delete': '#ec4899',
    'backup.create':   '#06b6d4',
    'backup.delete':   '#f59e0b',
    'account.provision': '#22c55e',
    'account.delete':    '#ef4444',
};

const AuditTab: React.FC = () => {
    const { showToast } = useToast();
    const [events, setEvents] = useState<AuditEvent[]>([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [filterAction, setFilterAction] = useState('');
    const [filterUserId, setFilterUserId] = useState('');
    const [page, setPage] = useState(0);
    const limit = 25;

    const fetchEvents = useCallback(async (pg = 0, action = filterAction, userId = filterUserId) => {
        setLoading(true);
        try {
            const params = new URLSearchParams({ limit: String(limit), offset: String(pg * limit) });
            if (action) params.set('action', action);
            if (userId) params.set('user_id', userId);
            const res = await axiosInstance.get(`/v1/admin/audit?${params}`);
            setEvents(res.data.events);
            setTotal(res.data.total);
        } catch {
            showToast('Failed to load audit log', 'error');
        } finally {
            setLoading(false);
        }
    }, [showToast, filterAction, filterUserId]);

    useEffect(() => { fetchEvents(); }, [fetchEvents]);

    const actionOptions = [
        '', 'instance', 'volume', 'snapshot', 'backup', 'account',
    ].map(a => ({ value: a, label: a || 'All actions' }));

    return (
        <div style={{ background: 'var(--surface-card)', border: '1px solid var(--border)', borderRadius: '10px', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid var(--border)', flexWrap: 'wrap', gap: '10px' }}>
                <span style={{ fontWeight: 600, fontSize: '0.95rem', color: 'var(--text-primary)' }}>Audit Log ({total})</span>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                    <input
                        type="text"
                        placeholder="Filter by user ID…"
                        value={filterUserId}
                        onChange={e => { setFilterUserId(e.target.value); setPage(0); fetchEvents(0, filterAction, e.target.value); }}
                        style={{ padding: '5px 10px', background: 'var(--surface-input)', color: 'var(--text-primary)', border: '1px solid var(--border)', borderRadius: '6px', fontSize: '0.8rem', width: '220px' }}
                    />
                    <select
                        value={filterAction}
                        onChange={e => { setFilterAction(e.target.value); setPage(0); fetchEvents(0, e.target.value, filterUserId); }}
                        style={{ padding: '5px 10px', background: 'var(--surface-input)', color: 'var(--text-primary)', border: '1px solid var(--border)', borderRadius: '6px', fontSize: '0.8rem' }}
                    >
                        {actionOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                    <button onClick={() => fetchEvents(page)} style={{ background: 'none', border: '1px solid var(--border)', borderRadius: '6px', padding: '5px 12px', cursor: 'pointer', fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                        <FaSyncAlt size={11} style={{ marginRight: '4px' }} /> Refresh
                    </button>
                </div>
            </div>
            <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                    <thead>
                        <tr style={{ background: 'var(--surface-alt)', borderBottom: '1px solid var(--border)' }}>
                            {['Time', 'User', 'Action', 'Resource', 'ID', 'IP'].map(h => (
                                <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>{h}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {loading && events.length === 0 && (
                            <tr><td colSpan={6} style={{ padding: '24px', textAlign: 'center', color: 'var(--text-muted)' }}>Loading…</td></tr>
                        )}
                        {!loading && events.length === 0 && (
                            <tr><td colSpan={6} style={{ padding: '24px', textAlign: 'center', color: 'var(--text-muted)' }}>No audit events yet.</td></tr>
                        )}
                        {events.map(e => (
                            <tr key={e.id} style={{ borderBottom: '1px solid var(--border)' }}>
                                <td style={{ padding: '9px 14px', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                                    {new Date(e.created_at).toLocaleString()}
                                </td>
                                <td style={{ padding: '9px 14px', fontWeight: 500, color: 'var(--text-primary)' }}>
                                    {e.username || <span style={{ color: 'var(--text-muted)', fontFamily: 'monospace', fontSize: '0.75rem' }}>{e.user_id.slice(0, 8)}…</span>}
                                </td>
                                <td style={{ padding: '9px 14px', whiteSpace: 'nowrap' }}>
                                    <span style={{
                                        background: `${ACTION_COLOR[e.action] || '#6b7280'}22`,
                                        color: ACTION_COLOR[e.action] || 'var(--text-muted)',
                                        borderRadius: '999px', padding: '2px 8px',
                                        fontSize: '0.75rem', fontWeight: 600,
                                    }}>{e.action}</span>
                                </td>
                                <td style={{ padding: '9px 14px', color: 'var(--text-muted)' }}>{e.resource_type || '-'}</td>
                                <td style={{ padding: '9px 14px', fontFamily: 'monospace', fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                                    {e.resource_id ? `${e.resource_id.slice(0, 8)}…` : '-'}
                                </td>
                                <td style={{ padding: '9px 14px', color: 'var(--text-muted)', fontFamily: 'monospace', fontSize: '0.75rem' }}>{e.ip_address || '-'}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            {total > limit && (
                <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', padding: '12px', borderTop: '1px solid var(--border)' }}>
                    <button onClick={() => { const p = Math.max(0, page - 1); setPage(p); fetchEvents(p); }} disabled={page === 0} style={{ padding: '5px 14px', background: 'none', border: '1px solid var(--border)', borderRadius: '6px', cursor: page === 0 ? 'not-allowed' : 'pointer', opacity: page === 0 ? 0.4 : 1, color: 'var(--text-primary)' }}>← Prev</button>
                    <span style={{ lineHeight: '30px', color: 'var(--text-muted)', fontSize: '0.8rem' }}>{page * limit + 1}-{Math.min((page + 1) * limit, total)} of {total}</span>
                    <button onClick={() => { const p = page + 1; setPage(p); fetchEvents(p); }} disabled={(page + 1) * limit >= total} style={{ padding: '5px 14px', background: 'none', border: '1px solid var(--border)', borderRadius: '6px', cursor: (page + 1) * limit >= total ? 'not-allowed' : 'pointer', opacity: (page + 1) * limit >= total ? 0.4 : 1, color: 'var(--text-primary)' }}>Next →</button>
                </div>
            )}
        </div>
    );
};

// ─── Pricing rules tab ────────────────────────────────────────────────────────

interface PricingRule {
    id: number;
    resource_type: string;
    flavor_name: string | null;
    hourly_rate: number;
    effective_from: string;
    effective_until: string | null;
    stripe_price_id: string | null;
}

const RESOURCE_TYPES = ['instance', 'volume', 'snapshot', 'backup', 'private_network', 'egress'] as const;
type ResourceType = typeof RESOURCE_TYPES[number];

const RESOURCE_COLORS: Record<string, string> = {
    instance:        'var(--status-info)',
    volume:          '#8b5cf6',
    snapshot:        '#f59e0b',
    backup:          '#06b6d4',
    private_network: '#22c55e',
    egress:          '#ef4444',
};

interface PricingRuleModalProps {
    rule: PricingRule | null;
    onClose: () => void;
    onSaved: () => void;
}

const PricingRuleModal: React.FC<PricingRuleModalProps> = ({ rule, onClose, onSaved }) => {
    const { showToast } = useToast();
    const isEdit = rule !== null;

    const [resourceType, setResourceType] = useState<ResourceType>(
        (rule?.resource_type as ResourceType) ?? 'instance'
    );
    const [flavorName, setFlavorName] = useState(rule?.flavor_name ?? '');
    const [hourlyRate, setHourlyRate] = useState(rule ? String(rule.hourly_rate) : '');
    const [effectiveFrom, setEffectiveFrom] = useState(
        rule ? new Date(rule.effective_from).toISOString().slice(0, 10) : new Date().toISOString().slice(0, 10)
    );
    const [effectiveUntil, setEffectiveUntil] = useState(
        rule?.effective_until ? new Date(rule.effective_until).toISOString().slice(0, 10) : ''
    );
    const [stripePriceId, setStripePriceId] = useState(rule?.stripe_price_id ?? '');
    const [saving, setSaving] = useState(false);

    const handleSave = async () => {
        if (!hourlyRate || isNaN(parseFloat(hourlyRate))) {
            showToast('Hourly rate must be a positive number', 'error');
            return;
        }
        setSaving(true);
        try {
            if (isEdit) {
                const body: any = { hourly_rate: parseFloat(hourlyRate) };
                body.effective_until = effectiveUntil || null;
                body.stripe_price_id = stripePriceId || null;
                await axiosInstance.put(`/v1/admin/pricing-rules/${rule!.id}`, body);
            } else {
                const body: any = {
                    resource_type: resourceType,
                    hourly_rate: parseFloat(hourlyRate),
                    effective_from: effectiveFrom,
                };
                if (resourceType === 'instance' && flavorName) body.flavor_name = flavorName;
                if (effectiveUntil) body.effective_until = effectiveUntil;
                if (stripePriceId) body.stripe_price_id = stripePriceId;
                await axiosInstance.post('/v1/admin/pricing-rules', body);
            }
            showToast(isEdit ? 'Rule updated' : 'Rule created', 'success');
            onSaved();
            onClose();
        } catch {
            showToast(isEdit ? 'Failed to update rule' : 'Failed to create rule', 'error');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
            <div style={{ background: 'var(--bg-card)', borderRadius: '12px', width: '480px', maxWidth: '95vw', overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 24px', borderBottom: '1px solid var(--border)' }}>
                    <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 700 }}>{isEdit ? 'Edit Pricing Rule' : 'New Pricing Rule'}</h3>
                    <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: '1.2rem' }}><FaTimes /></button>
                </div>
                <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
                    {!isEdit && (
                        <div>
                            <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: '5px', color: 'var(--text-secondary)' }}>Resource Type</label>
                            <select
                                value={resourceType}
                                onChange={e => setResourceType(e.target.value as ResourceType)}
                                style={{ width: '100%', padding: '8px 12px', border: '1px solid var(--border)', borderRadius: '6px', fontSize: '0.875rem', background: 'var(--bg-card)', color: 'var(--text-primary)' }}
                            >
                                {RESOURCE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                            </select>
                        </div>
                    )}
                    {!isEdit && resourceType === 'instance' && (
                        <div>
                            <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: '5px', color: 'var(--text-secondary)' }}>Flavor Name</label>
                            <input
                                type="text"
                                placeholder="e.g. m1.xlarge"
                                value={flavorName}
                                onChange={e => setFlavorName(e.target.value)}
                                style={{ width: '100%', boxSizing: 'border-box', padding: '8px 12px', border: '1px solid var(--border)', borderRadius: '6px', fontSize: '0.875rem' }}
                            />
                        </div>
                    )}
                    <div>
                        <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: '5px', color: 'var(--text-secondary)' }}>Hourly Rate (£)</label>
                        <input
                            type="number"
                            min="0.000001"
                            step="0.0001"
                            placeholder="e.g. 0.0972"
                            value={hourlyRate}
                            onChange={e => setHourlyRate(e.target.value)}
                            style={{ width: '100%', boxSizing: 'border-box', padding: '8px 12px', border: '1px solid var(--border)', borderRadius: '6px', fontSize: '0.875rem' }}
                        />
                    </div>
                    {!isEdit && (
                        <div>
                            <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: '5px', color: 'var(--text-secondary)' }}>Effective From</label>
                            <input
                                type="date"
                                value={effectiveFrom}
                                onChange={e => setEffectiveFrom(e.target.value)}
                                style={{ width: '100%', boxSizing: 'border-box', padding: '8px 12px', border: '1px solid var(--border)', borderRadius: '6px', fontSize: '0.875rem' }}
                            />
                        </div>
                    )}
                    <div>
                        <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: '5px', color: 'var(--text-secondary)' }}>Effective Until <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(optional)</span></label>
                        <input
                            type="date"
                            value={effectiveUntil}
                            onChange={e => setEffectiveUntil(e.target.value)}
                            style={{ width: '100%', boxSizing: 'border-box', padding: '8px 12px', border: '1px solid var(--border)', borderRadius: '6px', fontSize: '0.875rem' }}
                        />
                    </div>
                    <div>
                        <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: '5px', color: 'var(--text-secondary)' }}>Stripe Price ID <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(optional)</span></label>
                        <input
                            type="text"
                            placeholder="price_..."
                            value={stripePriceId}
                            onChange={e => setStripePriceId(e.target.value)}
                            style={{ width: '100%', boxSizing: 'border-box', padding: '8px 12px', border: '1px solid var(--border)', borderRadius: '6px', fontSize: '0.875rem' }}
                        />
                    </div>
                </div>
                <div style={{ padding: '16px 24px', borderTop: '1px solid var(--border)', display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                    <button onClick={onClose} style={{ padding: '8px 18px', background: 'none', border: '1px solid var(--border)', borderRadius: '6px', cursor: 'pointer', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>Cancel</button>
                    <button
                        onClick={handleSave}
                        disabled={saving || !hourlyRate}
                        style={{ padding: '8px 18px', background: 'var(--action-primary-bg)', color: 'var(--action-primary-text)', border: 'none', borderRadius: '6px', cursor: (saving || !hourlyRate) ? 'not-allowed' : 'pointer', opacity: (saving || !hourlyRate) ? 0.7 : 1, fontSize: '0.875rem', fontWeight: 500 }}
                    >
                        {saving ? 'Saving…' : (isEdit ? 'Update' : 'Create')}
                    </button>
                </div>
            </div>
        </div>
    );
};

const PricingRulesTab: React.FC = () => {
    const { showToast } = useToast();
    const [rules, setRules] = useState<PricingRule[]>([]);
    const [loading, setLoading] = useState(false);
    const [modalRule, setModalRule] = useState<PricingRule | null | undefined>(undefined);
    const [deletingId, setDeletingId] = useState<number | null>(null);

    const fetchRules = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axiosInstance.get('/v1/admin/pricing-rules');
            setRules(res.data);
        } catch {
            showToast('Failed to load pricing rules', 'error');
        } finally {
            setLoading(false);
        }
    }, [showToast]);

    useEffect(() => { fetchRules(); }, [fetchRules]);

    const handleDelete = async (id: number) => {
        setDeletingId(id);
        try {
            await axiosInstance.delete(`/v1/admin/pricing-rules/${id}`);
            showToast('Rule deleted', 'success');
            fetchRules();
        } catch {
            showToast('Failed to delete rule', 'error');
        } finally {
            setDeletingId(null);
        }
    };

    const fmtRate = (r: number) => `£${parseFloat(String(r)).toFixed(6)}/hr`;
    const fmtMonthly = (r: number) => `~£${(parseFloat(String(r)) * 730).toFixed(2)}/mo`;

    return (
        <>
            {modalRule !== undefined && (
                <PricingRuleModal
                    rule={modalRule}
                    onClose={() => setModalRule(undefined)}
                    onSaved={fetchRules}
                />
            )}
            <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '10px', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid var(--border)' }}>
                    <span style={{ fontWeight: 600, fontSize: '0.95rem' }}>Pricing Rules ({rules.length})</span>
                    <div style={{ display: 'flex', gap: '8px' }}>
                        <button onClick={fetchRules} disabled={loading} style={{ background: 'none', border: '1px solid var(--border)', borderRadius: '6px', padding: '5px 12px', cursor: 'pointer', fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                            <FaSyncAlt size={11} style={{ marginRight: '4px' }} /> Refresh
                        </button>
                        <button onClick={() => setModalRule(null)} style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '5px 14px', background: 'var(--action-primary-bg)', color: 'var(--action-primary-text)', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 500 }}>
                            <FaPlus size={11} /> New Rule
                        </button>
                    </div>
                </div>
                <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                        <thead>
                            <tr style={{ background: 'var(--bg-table)', borderBottom: '1px solid var(--border)' }}>
                                {['ID', 'Resource Type', 'Flavor', 'Hourly Rate', 'Monthly Est.', 'Effective From', 'Effective Until', 'Stripe Price ID', 'Actions'].map(h => (
                                    <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {loading && rules.length === 0 && (
                                <tr><td colSpan={9} style={{ padding: '32px', textAlign: 'center', color: 'var(--text-muted)' }}>Loading…</td></tr>
                            )}
                            {!loading && rules.length === 0 && (
                                <tr><td colSpan={9} style={{ padding: '32px', textAlign: 'center', color: 'var(--text-muted)' }}>No pricing rules found.</td></tr>
                            )}
                            {rules.map(r => {
                                const expired = r.effective_until && new Date(r.effective_until) < new Date();
                                return (
                                    <tr key={r.id} style={{ borderBottom: '1px solid var(--border)', opacity: expired ? 0.5 : 1 }}>
                                        <td style={{ padding: '10px 14px', fontFamily: 'monospace', color: 'var(--text-muted)' }}>{r.id}</td>
                                        <td style={{ padding: '10px 14px' }}>
                                            <span style={{ background: `${RESOURCE_COLORS[r.resource_type] || '#6b7280'}22`, color: RESOURCE_COLORS[r.resource_type] || '#6b7280', borderRadius: '999px', padding: '2px 10px', fontWeight: 600, whiteSpace: 'nowrap' }}>
                                                {r.resource_type}
                                            </span>
                                        </td>
                                        <td style={{ padding: '10px 14px', fontFamily: 'monospace', color: r.flavor_name ? 'var(--text-primary)' : 'var(--text-muted)' }}>
                                            {r.flavor_name || '-'}
                                        </td>
                                        <td style={{ padding: '10px 14px', fontWeight: 600, fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
                                            {fmtRate(r.hourly_rate)}
                                        </td>
                                        <td style={{ padding: '10px 14px', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                                            {fmtMonthly(r.hourly_rate)}
                                        </td>
                                        <td style={{ padding: '10px 14px', whiteSpace: 'nowrap', color: 'var(--text-muted)' }}>
                                            {new Date(r.effective_from).toLocaleDateString()}
                                        </td>
                                        <td style={{ padding: '10px 14px', whiteSpace: 'nowrap', color: expired ? '#ef4444' : 'var(--text-muted)' }}>
                                            {r.effective_until ? new Date(r.effective_until).toLocaleDateString() : '-'}
                                        </td>
                                        <td style={{ padding: '10px 14px', fontFamily: 'monospace', fontSize: '0.75rem', color: r.stripe_price_id ? 'var(--text-primary)' : 'var(--text-muted)' }}>
                                            {r.stripe_price_id || '-'}
                                        </td>
                                        <td style={{ padding: '10px 14px', whiteSpace: 'nowrap' }}>
                                            <div style={{ display: 'flex', gap: '6px' }}>
                                                <button
                                                    onClick={() => setModalRule(r)}
                                                    style={{ padding: '3px 10px', background: 'none', border: '1px solid var(--border)', borderRadius: '5px', cursor: 'pointer', fontSize: '0.75rem', color: 'var(--text-secondary)' }}
                                                >
                                                    Edit
                                                </button>
                                                <button
                                                    onClick={() => handleDelete(r.id)}
                                                    disabled={deletingId === r.id}
                                                    style={{ padding: '3px 10px', background: 'none', border: '1px solid #fca5a5', borderRadius: '5px', cursor: deletingId === r.id ? 'not-allowed' : 'pointer', fontSize: '0.75rem', color: '#ef4444', opacity: deletingId === r.id ? 0.5 : 1 }}
                                                >
                                                    Delete
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
                <p style={{ padding: '10px 20px', margin: 0, fontSize: '0.72rem', color: 'var(--text-muted)', borderTop: '1px solid var(--border)' }}>
                    Monthly estimates use 730 hr/month. Expired rules (effective_until in the past) are shown at reduced opacity.
                </p>
            </div>
        </>
    );
};

// ─── Credits tab ─────────────────────────────────────────────────────────────

const CREDIT_TYPES = ['manual', 'promotional', 'referral', 'compensation'] as const;
type CreditType = typeof CREDIT_TYPES[number];

const CreditsTab: React.FC = () => {
    const { showToast } = useToast();
    const [credits, setCredits] = useState<AdminCreditRow[]>([]);
    const [loading, setLoading] = useState(false);
    const [filterUserId, setFilterUserId] = useState('');
    const [grantUserId, setGrantUserId] = useState('');
    const [grantAmount, setGrantAmount] = useState('');
    const [grantExpiresAt, setGrantExpiresAt] = useState('');
    const [grantType, setGrantType] = useState<CreditType>('manual');
    const [granting, setGranting] = useState(false);
    const grantRequest = useRef<{body:string;key:string}|null>(null);

    const fetchCredits = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axiosInstance.get('/v1/admin/credits');
            setCredits(res.data);
        } catch {
            showToast('Failed to load credits', 'error');
        } finally {
            setLoading(false);
        }
    }, [showToast]);

    useEffect(() => { fetchCredits(); }, [fetchCredits]);

    const filtered = filterUserId.trim()
        ? credits.filter(c => c.user_id.toLowerCase().includes(filterUserId.trim().toLowerCase()))
        : credits;
    const grantTarget = credits.find(c => c.user_id === grantUserId.trim());
    const grantTargetHasNoStripeCustomer = Boolean(grantTarget && grantTarget.credit_source!=='account_ledger' && !grantTarget.stripe_customer_id);

    const handleGrant = async () => {
        if (grantTargetHasNoStripeCustomer || !grantUserId || !grantAmount || !grantExpiresAt) return;
        setGranting(true);
        const body={user_id:grantUserId,amount:parseFloat(grantAmount),expires_at:grantExpiresAt,type:grantType};
        const fingerprint=JSON.stringify(body);
        if(grantRequest.current?.body!==fingerprint)grantRequest.current={body:fingerprint,key:crypto.randomUUID()};
        try {
            await axiosInstance.post('/v1/admin/credits',body,{headers:{'Idempotency-Key':grantRequest.current!.key}});
            grantRequest.current=null;
            showToast('Credit granted', 'success');
            setGrantUserId('');
            setGrantAmount('');
            setGrantExpiresAt('');
            fetchCredits();
        } catch {
            showToast('Failed to grant credit', 'error');
        } finally {
            setGranting(false);
        }
    };

    const handleRevoke = async (id: string | number) => {
        try {
            await axiosInstance.delete(`/v1/admin/credits/${id}`);
            showToast('Credit revoked', 'success');
            fetchCredits();
        } catch {
            showToast('Failed to revoke credit', 'error');
        }
    };

    const inputStyle: React.CSSProperties = {
        width: '100%', boxSizing: 'border-box', padding: '7px 10px',
        border: '1px solid var(--border)', borderRadius: '6px',
        fontSize: '0.875rem', background: 'var(--bg-card)', color: 'var(--text-primary)',
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '10px', padding: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
                <p style={{ margin: '0 0 4px', fontWeight: 600, fontSize: '0.9rem' }}>Grant paid-usage credit</p>
                <p style={{ margin: '0 0 12px', fontSize: '0.75rem', color: 'var(--text-muted)' }}>Applies to eligible paid resource usage. Plan commitments are charged separately and come back as their own commitment credits. A date expires at the end of that UTC day.</p>
                {grantTargetHasNoStripeCustomer && (
                    <p role="status" style={{ margin: '0 0 12px', fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                        Disabled: this user is on a no-card trial and has no Stripe billing profile.
                    </p>
                )}
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                    <div style={{ flex: '2', minWidth: '200px' }}>
                        <label style={{ display: 'block', fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: '4px' }}>User ID (Keycloak)</label>
                        <input
                            type="text"
                            placeholder="User ID…"
                            value={grantUserId}
                            onChange={e => setGrantUserId(e.target.value)}
                            style={inputStyle}
                        />
                    </div>
                    <div style={{ flex: '1', minWidth: '110px' }}>
                        <label style={{ display: 'block', fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: '4px' }}>Amount (£)</label>
                        <input
                            type="number"
                            min="0.01"
                            step="0.01"
                            placeholder="0.00"
                            value={grantAmount}
                            onChange={e => setGrantAmount(e.target.value)}
                            style={inputStyle}
                        />
                    </div>
                    <div style={{ flex: '1', minWidth: '130px' }}>
                        <label style={{ display: 'block', fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: '4px' }}>Expires (UTC day)</label>
                        <input
                            type="date"
                            value={grantExpiresAt}
                            onChange={e => setGrantExpiresAt(e.target.value)}
                            style={inputStyle}
                        />
                    </div>
                    <div style={{ flex: '1', minWidth: '130px' }}>
                        <label style={{ display: 'block', fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: '4px' }}>Type</label>
                        <select
                            value={grantType}
                            onChange={e => setGrantType(e.target.value as CreditType)}
                            style={inputStyle}
                        >
                            {CREDIT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                        </select>
                    </div>
                    <button
                        onClick={handleGrant}
                        disabled={grantTargetHasNoStripeCustomer || granting || !grantUserId || !grantAmount || !grantExpiresAt}
                        style={{
                            display: 'flex', alignItems: 'center', gap: '6px',
                            padding: '8px 16px', background: 'var(--action-primary-bg)', color: 'var(--action-primary-text)',
                            border: 'none', borderRadius: '6px', fontSize: '0.875rem', fontWeight: 500,
                            cursor: (grantTargetHasNoStripeCustomer || granting || !grantUserId || !grantAmount || !grantExpiresAt) ? 'not-allowed' : 'pointer',
                            opacity: (grantTargetHasNoStripeCustomer || granting || !grantUserId || !grantAmount || !grantExpiresAt) ? 0.7 : 1,
                            whiteSpace: 'nowrap',
                        }}
                    >
                        <FaPlus size={11} /> {granting ? 'Granting…' : 'Grant'}
                    </button>
                </div>
            </div>

            <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '10px', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid var(--border)', flexWrap: 'wrap', gap: '10px' }}>
                    <span style={{ fontWeight: 600, fontSize: '0.95rem' }}>
                        Credits ({filtered.length}{filterUserId ? ' filtered' : ' total'})
                    </span>
                    <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                        <input
                            type="text"
                            placeholder="Filter by user ID…"
                            value={filterUserId}
                            onChange={e => setFilterUserId(e.target.value)}
                            style={{ padding: '5px 10px', background: 'var(--bg-card)', color: 'var(--text-primary)', border: '1px solid var(--border)', borderRadius: '6px', fontSize: '0.8rem', width: '240px' }}
                        />
                        <button onClick={fetchCredits} disabled={loading} style={{ background: 'none', border: '1px solid var(--border)', borderRadius: '6px', padding: '5px 12px', cursor: 'pointer', fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                            <FaSyncAlt size={11} style={{ marginRight: '4px' }} /> Refresh
                        </button>
                    </div>
                </div>
                <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                        <thead>
                            <tr style={{ background: 'var(--bg-table)', borderBottom: '1px solid var(--border)' }}>
                                {['User ID', 'OS ID', 'Amount', 'Used', 'Remaining', 'Type', 'Granted', 'Expires', 'Actions'].map(h => (
                                    <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {loading && credits.length === 0 && (
                                <tr><td colSpan={9} style={{ padding: '24px', textAlign: 'center', color: 'var(--text-muted)' }}>Loading…</td></tr>
                            )}
                            {!loading && filtered.length === 0 && (
                                <tr><td colSpan={9} style={{ padding: '24px', textAlign: 'center', color: 'var(--text-muted)' }}>
                                    {filterUserId ? 'No credits matching this user ID.' : 'No credits found.'}
                                </td></tr>
                            )}
                            {filtered.map(c => (
                                <tr key={c.id} style={{ borderBottom: '1px solid var(--border)', opacity: c.is_expired ? 0.5 : 1 }}>
                                    <td style={{ padding: '9px 14px', fontFamily: 'monospace', fontSize: '0.75rem' }}>
                                        <span title={c.user_id}>{shortId(c.user_id)}</span>
                                        <CopyButton text={c.user_id} />
                                    </td>
                                    <td style={{ padding: '9px 14px', fontFamily: 'monospace', fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                                        {c.os_id ? <><span title={c.os_id}>{shortId(c.os_id)}</span><CopyButton text={c.os_id} /></> : '-'}
                                    </td>
                                    <td style={{ padding: '9px 14px' }}>£{parseFloat(String(c.amount)).toFixed(2)}</td>
                                    <td style={{ padding: '9px 14px', color: 'var(--text-muted)' }}>£{parseFloat(String(c.used)).toFixed(4)}</td>
                                    <td style={{ padding: '9px 14px', fontWeight: parseFloat(String(c.remaining)) > 0 ? 600 : 400, color: parseFloat(String(c.remaining)) > 0 ? '#15803d' : 'var(--text-muted)' }}>
                                        £{parseFloat(String(c.remaining)).toFixed(4)}
                                    </td>
                                    <td style={{ padding: '9px 14px' }}>
                                        <span style={{ background: '#6b728022', color: 'var(--text-secondary)', borderRadius: '999px', padding: '2px 8px', fontSize: '0.75rem' }}>
                                            {c.type === 'purchased' ? 'commitment' : c.type}{c.credit_source === 'legacy_local' ? ' (legacy)' : ''}
                                        </span>
                                    </td>
                                    <td style={{ padding: '9px 14px', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                                        {new Date(c.granted_at).toLocaleDateString()}
                                    </td>
                                    <td style={{ padding: '9px 14px', whiteSpace: 'nowrap', color: c.is_expired ? '#ef4444' : 'var(--text-muted)' }}>
                                        {c.expires_at ? new Date(c.expires_at).toLocaleDateString() : '-'}
                                        {c.is_expired && <span style={{ marginLeft: '4px', fontSize: '0.7rem' }}>(expired)</span>}
                                    </td>
                                    <td style={{ padding: '9px 14px' }}>
                                        {!c.is_expired && (
                                            <button
                                                onClick={() => handleRevoke(c.id)}
                                                style={{ padding: '3px 8px', background: 'none', border: '1px solid #fca5a5', borderRadius: '4px', color: '#ef4444', cursor: 'pointer', fontSize: '0.75rem' }}
                                            >
                                                Revoke
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

// ─── Users tab ────────────────────────────────────────────────────────────────

interface AdminUserRow {
    paymentRecovery?: import('../store/slices/paymentSlice').PaymentRecovery | null;
    user_id: string;
    username: string;
    os_id: string;
    os_project: string;
    created_at: string;
    region_id: string | null;
    billing_status: string | null;
    stripe_customer_id: string | null;
    credits_remaining: number;
    credits_expire_at: string | null;
    is_internal: boolean;
    suspended_by_admin: boolean;
    suspended_at: string | null;
    suspended_reason: string | null;
    // Card-refusal state. card_blocked = a never-verified account refused outright (creates are
    // returning 402). card_grace_until = an established account on the 7 day clock; it keeps full
    // access until then, and the hourly sweep suspends it at the deadline.
    card_blocked?: boolean;
    card_grace_until?: string | null;
    card_verified_at?: string | null;
    card_decline_code?: string | null;
    returning_from?: ReturningFrom | null;
    active_instances: number;
    active_volumes: number;
    plan_label?: string;
    plan_label_inputs?: PlanLabelInputs | null;
}

const BILLING_STATUS_OPTIONS = ['', 'active', 'past_due', 'for_deletion', 'canceled', 'trial'];

interface AdminRecoveryRow {
    userId: string;
    customerId?: string;
    currentCustomerId?: string | null;
    username?: string | null;
    workflowId?: string;
    phase: string;
    deletionAt?: string | null;
    firstFailedAt?: string | null;
    failureDeadlineAt?: string | null;
    category?: 'recovery' | 'review' | 'checking' | 'inactive';
    reconciliation?: { status: string; reason: string | null; lastCheckedAt: string | null;
        lastVerifiedAt: string | null; nextCheckAt: string | null };
    invoiceIds: string[];
    verifiedInvoiceIds?: string[] | null;
    progress: Record<string, { complete: boolean; error?: string }>;
    error?: string;
    notificationFailures: Array<{ event: string; error: string }>;
}

const InvoiceRecoveryPanel: React.FC<{ rows: AdminRecoveryRow[] }> = ({ rows }) => {
    // The fallback also fixes the misleading legacy response during an API/console rolling deploy.
    const category = (r: AdminRecoveryRow) => r.category ?? (
        ['recovered', 'deleted'].includes(r.phase) ? 'inactive'
            : r.invoiceIds.length || ['suspending', 'suspended', 'recovering', 'deleting'].includes(r.phase) ? 'recovery'
                : r.error ? 'review' : 'checking');
    const groups = [
        { key: 'recovery', title: 'Invoice recovery and incomplete cleanup', explanation: 'Recorded subscription debt or unfinished recovery actions. Stripe is checked again before deletion.' },
        { key: 'review', title: 'Accounts needing review', explanation: 'Lookup or ownership errors are not proof of unpaid invoices. A lookup error cannot authorize deletion. Do not remove mappings until account and resource cleanup is verified.' },
        { key: 'checking', title: 'Awaiting invoice verification', explanation: 'These accounts have not yet been verified as having outstanding subscription debt.' },
    ];
    const timestamp = (label: string, value?: string | null) => value && <p>{label}: <time dateTime={value}>{new Date(value).toLocaleString()}</time></p>;
    return <>
        {groups.map(group => {
            const entries = rows.filter(r => category(r) === group.key);
            if (!entries.length) return null;
            return <details key={group.key} style={{ padding: '16px 20px' }}>
                <summary>{group.title} ({entries.length})</summary>
                <p>{group.explanation}</p>
                {entries.map(r => {
                    const verified = r.reconciliation?.status === 'verified';
                    const checkedInvoices = r.verifiedInvoiceIds ?? r.invoiceIds;
                    const state = group.key === 'review' ? 'Needs review' : group.key === 'checking' ? 'Awaiting verification' : r.phase;
                    return <section key={r.userId} aria-label={`Recovery record ${r.userId}`} style={{ padding: '10px 0', borderTop: '1px solid var(--border)', overflowWrap: 'anywhere' }}>
                        <strong>{r.username || r.userId}: {state}</strong>
                        <p>User ID: {r.userId}<CopyButton text={r.userId} /></p>
                        {r.customerId && <p>Stripe customer: {r.customerId}<CopyButton text={r.customerId} /></p>}
                        {r.currentCustomerId && r.currentCustomerId !== r.customerId && <p>Current customer mapping: {r.currentCustomerId}</p>}
                        {timestamp('First failed payment', r.firstFailedAt)}
                        {timestamp('72-hour cutoff (eligibility, not a scheduled deletion)', r.failureDeadlineAt)}
                        {timestamp('Deletion deadline', r.deletionAt)}
                        <p>{verified ? 'Outstanding invoices' : 'Last recorded invoices'}: {checkedInvoices.length ? checkedInvoices.join(', ') : verified ? 'none' : 'unknown - not verified'}</p>
                        {r.invoiceIds.length > 0 && r.invoiceIds.join(',') !== checkedInvoices.join(',') && <p>Episode invoice references: {r.invoiceIds.join(', ')}</p>}
                        {!verified && <p>Current invoice status is not verified. Recorded invoices are retained for reconciliation.</p>}
                        {r.reconciliation?.reason && <p>Review reason: {r.reconciliation.reason.replace(/_/g, ' ')}</p>}
                        {r.error && <p role="alert">{r.error}</p>}
                        {timestamp('Last check', r.reconciliation?.lastCheckedAt)}
                        {timestamp('Last successful Stripe check', r.reconciliation?.lastVerifiedAt)}
                        {timestamp('Next reconciliation check', r.reconciliation?.nextCheckAt)}
                        {r.workflowId && <p>Workflow: {r.workflowId}<CopyButton text={r.workflowId} /></p>}
                        {Object.entries(r.progress).filter(([, s]) => !s.complete).map(([name, s]) => <p key={name}>Incomplete: {name}{s.error ? ` - ${s.error}` : ''}</p>)}
                        {r.notificationFailures.map(n => <p key={n.event}>Notification pending: {n.event} - {n.error}</p>)}
                    </section>;
                })}
            </details>;
        })}
    </>;
};

const UsersTab: React.FC<{
    onOpenCredits: (userId: string, stripeCustomerId: string | null) => void;
    holds: Record<string, DeleteHold>;
    onRefreshHolds: () => void;
}> = ({ onOpenCredits, holds, onRefreshHolds }) => {
    const { showToast } = useToast();
    const [rows, setRows] = useState<AdminUserRow[]>([]);
    const [recoveries, setRecoveries] = useState<AdminRecoveryRow[]>([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(0);
    const [filterStatus, setFilterStatus] = useState('');
    // Internal accounts are hidden by default: Milos and Alex hold several test accounts each and
    // they otherwise sit at the top of every list, so the customer view is mostly founders.
    const [showInternal, setShowInternal] = useState(false);
    const limit = 25;

    const fetchRows = useCallback(async (pg = 0, status = filterStatus, internal = showInternal) => {
        setLoading(true);
        try {
            const params = new URLSearchParams({ limit: String(limit), offset: String(pg * limit) });
            if (status) params.set('billing_status', status);
            if (internal) params.set('include_internal', '1');
            const res = await axiosInstance.get(`/v1/admin/users?${params}`);
            setRows(res.data.users);
            setRecoveries(res.data.paymentRecoveries ?? []);
            setTotal(res.data.total);
        } catch {
            showToast('Failed to load users', 'error');
        } finally {
            setLoading(false);
        }
    }, [showToast, filterStatus, showInternal]);

    useEffect(() => { fetchRows(); }, [fetchRows]);

    return (
        <div style={{ background: 'var(--surface-card)', border: '1px solid var(--border)', borderRadius: '10px', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
            <InvoiceRecoveryPanel rows={recoveries} />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid var(--border)', flexWrap: 'wrap', gap: '10px' }}>
                <span style={{ fontWeight: 600, fontSize: '0.95rem', color: 'var(--text-primary)' }}>Users ({total})</span>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                    <select
                        value={filterStatus}
                        onChange={e => { setFilterStatus(e.target.value); setPage(0); fetchRows(0, e.target.value); }}
                        style={{ padding: '5px 10px', background: 'var(--surface-input)', color: 'var(--text-primary)', border: '1px solid var(--border)', borderRadius: '6px', fontSize: '0.8rem' }}
                    >
                        {BILLING_STATUS_OPTIONS.map(s => (
                            <option key={s} value={s}>{s || 'All billing statuses'}</option>
                        ))}
                    </select>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.8rem', color: 'var(--text-secondary)', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                        <input
                            type="checkbox"
                            checked={showInternal}
                            onChange={e => { setShowInternal(e.target.checked); setPage(0); fetchRows(0, filterStatus, e.target.checked); }}
                        />
                        Show internal
                    </label>
                    <button onClick={() => { fetchRows(page); onRefreshHolds(); }} style={{ background: 'none', border: '1px solid var(--border)', borderRadius: '6px', padding: '5px 12px', cursor: 'pointer', fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                        <FaSyncAlt size={11} style={{ marginRight: '4px' }} /> Refresh
                    </button>
                </div>
            </div>
            <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                    <thead>
                        <tr style={{ background: 'var(--surface-alt)', borderBottom: '1px solid var(--border)' }}>
                            {['User', 'OS User ID', 'OS Project', 'Joined', 'Plan / Status', 'Credits', 'Credits Expire', 'Instances', 'Volumes', 'Actions'].map(h => (
                                <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>{h}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {loading && rows.length === 0 && (
                            <tr><td colSpan={10} style={{ padding: '24px', textAlign: 'center', color: 'var(--text-muted)' }}>Loading…</td></tr>
                        )}
                        {!loading && rows.length === 0 && (
                            <tr><td colSpan={10} style={{ padding: '24px', textAlign: 'center', color: 'var(--text-muted)' }}>No users found.</td></tr>
                        )}
                        {rows.map(u => (
                            <tr key={u.user_id} style={{ borderBottom: '1px solid var(--border)' }}>
                                <td style={{ padding: '9px 14px', fontWeight: 500, color: 'var(--text-primary)', whiteSpace: 'nowrap' }}>
                                    {u.username || <span style={{ color: 'var(--text-muted)', fontFamily: 'monospace', fontSize: '0.75rem' }}>{shortId(u.user_id)}</span>}
                                    <CopyButton text={u.user_id} />
                                    {u.is_internal && (
                                        <span title="redu's own account. Excluded from customer counts and revenue."
                                              style={{ marginLeft: 6, background: 'var(--surface-alt)', color: 'var(--text-muted)', border: '1px solid var(--border)', borderRadius: 999, padding: '1px 7px', fontSize: '0.68rem', fontWeight: 600 }}>
                                            internal
                                        </span>
                                    )}
                                    {u.suspended_by_admin && (
                                        <span title={u.suspended_reason ? `Suspended: ${u.suspended_reason}` : 'Suspended by an admin'}
                                              style={{ marginLeft: 6, background: '#ef444422', color: '#ef4444', borderRadius: 999, padding: '1px 7px', fontSize: '0.68rem', fontWeight: 700 }}>
                                            suspended
                                        </span>
                                    )}
                                    {holds[u.user_id] && <DeleteHeldBadge hold={holds[u.user_id]} />}
                                    <ReturningPill from={u.returning_from} />
                                    {/* The card-refusal clock. Without a badge the 7 day grace runs
                                        invisibly and the auto-suspension arrives with no warning. */}
                                    {u.card_blocked && (
                                        <span title={`Card refused${u.card_decline_code ? ` (${u.card_decline_code})` : ''}. This account has never had a card approved, so creates are refused until one is.`}
                                              style={{ marginLeft: 6, background: '#ef444422', color: '#ef4444', borderRadius: 999, padding: '1px 7px', fontSize: '0.68rem', fontWeight: 700 }}>
                                            card blocked
                                        </span>
                                    )}
                                    {!u.card_blocked && u.card_grace_until && (() => {
                                        const ms = new Date(u.card_grace_until).getTime() - Date.now();
                                        const days = Math.max(0, Math.ceil(ms / 86400000));
                                        return (
                                            <span title={`Card refused${u.card_decline_code ? ` (${u.card_decline_code})` : ''}. This account has paid before, so it keeps full access until ${new Date(u.card_grace_until!).toLocaleString()}, then it is auto-suspended for review.`}
                                                  style={{ marginLeft: 6, background: '#f59e0b22', color: '#b45309', borderRadius: 999, padding: '1px 7px', fontSize: '0.68rem', fontWeight: 700 }}>
                                                {days > 0 ? `card grace ${days}d` : 'card grace due'}
                                            </span>
                                        );
                                    })()}
                                </td>
                                <td style={{ padding: '9px 14px', fontFamily: 'monospace', fontSize: '0.75rem', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                                    <span title={u.os_id}>{shortId(u.os_id)}</span><CopyButton text={u.os_id} />
                                </td>
                                <td style={{ padding: '9px 14px', fontFamily: 'monospace', fontSize: '0.75rem', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                                    <span title={u.os_project}>{shortId(u.os_project)}</span><CopyButton text={u.os_project} />
                                </td>
                                <td style={{ padding: '9px 14px', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                                    {new Date(u.created_at).toLocaleDateString()}
                                </td>
                                <td style={{ padding: '9px 14px', whiteSpace: 'nowrap' }}>
                                    {/* ⛔ The SAME pill as the Billing tab. Rendering bc.status raw
                                        here reproduced both conflations that tab was already fixed
                                        for: an amber "past_due" when no invoice ever failed, and a
                                        "suspended" badge when no admin ever suspended anything. */}
                                    <PlanPill label={u.plan_label} inputs={u.plan_label_inputs} /><FreeTierMark inputs={u.plan_label_inputs} />
                                    {(() => {
                                        const st = ((u as any).state ?? 'inactive') as AccountState;
                                        const meta = ACCOUNT_STATE[st] ?? ACCOUNT_STATE.inactive;
                                        return (
                                            <span title={meta.hint} style={{ background: `${meta.colour}22`, color: meta.colour, borderRadius: '999px', padding: '2px 10px', fontSize: '0.75rem', fontWeight: 700 }}>
                                                {meta.label}
                                            </span>
                                        );
                                    })()}
                                </td>
                                <td style={{ padding: '9px 14px', color: u.credits_remaining > 0 ? '#15803d' : 'var(--text-muted)', fontWeight: u.credits_remaining > 0 ? 600 : 400, whiteSpace: 'nowrap' }}>
                                    {u.credits_remaining > 0 ? `£${parseFloat(String(u.credits_remaining)).toFixed(4)}` : '-'}
                                </td>
                                <td style={{ padding: '9px 14px', whiteSpace: 'nowrap', fontSize: '0.78rem' }}>
                                    {u.credits_expire_at ? (() => {
                                        // The date this account stops being propped up by credit, which is
                                        // when it either starts paying or stops working. Soonest-expiring
                                        // grant that still has balance.
                                        const days = Math.ceil((new Date(u.credits_expire_at).getTime() - Date.now()) / 86400000);
                                        const colour = days <= 3 ? '#ef4444' : days <= 14 ? '#f59e0b' : 'var(--text-muted)';
                                        return (
                                            <span style={{ color: colour, fontWeight: days <= 14 ? 600 : 400 }}
                                                  title={new Date(u.credits_expire_at).toLocaleString()}>
                                                {new Date(u.credits_expire_at).toLocaleDateString()}
                                                <span style={{ opacity: 0.8 }}> ({days}d)</span>
                                            </span>
                                        );
                                    })() : <span style={{ color: 'var(--text-muted)' }}>-</span>}
                                </td>
                                <td style={{ padding: '9px 14px', textAlign: 'center' }}>
                                    <span style={{ background: u.active_instances > 0 ? 'var(--surface-selected)' : 'var(--surface-alt)', color: u.active_instances > 0 ? 'var(--text-primary)' : 'var(--text-muted)', borderRadius: '999px', padding: '2px 8px', fontWeight: 600, whiteSpace: 'nowrap' }}>
                                        {u.active_instances}
                                    </span>
                                </td>
                                <td style={{ padding: '9px 14px', textAlign: 'center', color: 'var(--text-muted)' }}>
                                    {u.active_volumes}
                                </td>
                                <td style={{ padding: '9px 14px', whiteSpace: 'nowrap' }}>
                                    <AccountActions
                                        userId={u.user_id}
                                        username={u.username}
                                        isInternal={!!u.is_internal}
                                        suspendedByAdmin={!!u.suspended_by_admin}
                                        onDone={() => { fetchRows(page); onRefreshHolds(); }}
                                        onCredits={() => onOpenCredits(u.user_id, u.stripe_customer_id)}
                                    />
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            {total > limit && (
                <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', padding: '12px', borderTop: '1px solid var(--border)' }}>
                    <button onClick={() => { const p = Math.max(0, page - 1); setPage(p); fetchRows(p); }} disabled={page === 0} style={{ padding: '5px 14px', background: 'none', border: '1px solid var(--border)', borderRadius: '6px', cursor: page === 0 ? 'not-allowed' : 'pointer', opacity: page === 0 ? 0.4 : 1, color: 'var(--text-primary)' }}>← Prev</button>
                    <span style={{ lineHeight: '30px', color: 'var(--text-muted)', fontSize: '0.8rem' }}>{page * limit + 1}-{Math.min((page + 1) * limit, total)} of {total}</span>
                    <button onClick={() => { const p = page + 1; setPage(p); fetchRows(p); }} disabled={(page + 1) * limit >= total} style={{ padding: '5px 14px', background: 'none', border: '1px solid var(--border)', borderRadius: '6px', cursor: (page + 1) * limit >= total ? 'not-allowed' : 'pointer', opacity: (page + 1) * limit >= total ? 0.4 : 1, color: 'var(--text-primary)' }}>Next →</button>
                </div>
            )}
        </div>
    );
};

// ─── Main page ─────────────────────────────────────────────────────────────

const AdminPage: React.FC = () => {
    const { showToast } = useToast();
    const [activeTab, setActiveTab] = useState<'billing' | 'users' | 'credits' | 'provisioning' | 'audit' | 'pricing'>('billing');
    const [users, setUsers] = useState<UserBillingRow[]>([]);
    const [summary, setSummary] = useState<Summary | null>(null);
    const [loading, setLoading] = useState(false);
    const [creditsPanelUser, setCreditsPanelUser] = useState<{ userId: string; stripeCustomerId: string | null } | null>(null);

    // Internal (redu's own) accounts are hidden by default. The TOTALS never count them either way;
    // this only controls whether they are listed, so finding one to un-flag cannot move the numbers.
    const [showInternal, setShowInternal] = useState(false);
    // Client-side, because the state is DERIVED from several columns the server already returns.
    // Pushing it into SQL would duplicate the rules and let the two drift.
    const [stateFilter, setStateFilter] = useState<'' | AccountState>('');

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axiosInstance.get(`/v1/admin/billing${showInternal ? '?include_internal=1' : ''}`);
            setUsers(res.data.users);
            setSummary(res.data.summary);
        } catch {
            showToast('Failed to load admin billing data', 'error');
        } finally {
            setLoading(false);
        }
    }, [showToast, showInternal]);

    useEffect(() => { fetchData(); }, [fetchData]);

    // ROADMAP 63 (Q4): the admin deletes a name held, by account. A held delete ran nothing irreversible, so the account
    // is still listed and still billing; without this the console only ever said "Teardown started".
    const [holds, setHolds] = useState<Record<string, DeleteHold>>({});
    const fetchHolds = useCallback(async () => {
        try {
            const res = await axiosInstance.get('/v1/admin/delete-holds');
            const byUser: Record<string, DeleteHold> = {};
            for (const h of (res.data?.holds ?? []) as DeleteHold[]) byUser[h.user_id] = h;
            setHolds(byUser);
        } catch {
            showToast('Failed to load the held account deletes', 'error');
        }
    }, [showToast]);
    useEffect(() => { fetchHolds(); }, [fetchHolds]);

    const visibleUsers = stateFilter ? users.filter(u => accountState(u) === stateFilter) : users;
    // ⛔ COUNTED OVER CUSTOMERS ONLY, exactly like the funnel tiles above. With "Show internal"
    // ticked the payload includes redu's own accounts, and reducing over the raw list made the
    // dropdown and the tile carrying the identical label disagree on the same screen.
    const countable = users.filter(u => !u.is_internal);
    const stateCounts = countable.reduce((acc, u) => {
        const st = accountState(u);
        acc[st] = (acc[st] ?? 0) + 1;
        return acc;
    }, {} as Record<string, number>);

    return (
        <div className="admin-page">
            {creditsPanelUser && (
                <CreditsPanel userId={creditsPanelUser.userId} stripeCustomerId={creditsPanelUser.stripeCustomerId} onClose={() => setCreditsPanelUser(null)} />
            )}

            <div className="admin-page__header">
                <div className="admin-page__navigation">
                    <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 700 }}>Admin</h2>
                    <div className="admin-page__tabs">
                        {(['billing', 'users', 'credits', 'provisioning', 'audit', 'pricing'] as const).map(tab => (
                            <button key={tab} onClick={() => setActiveTab(tab)} style={{ padding: '5px 14px', borderRadius: '6px', border: 'none', cursor: 'pointer', fontWeight: 500, fontSize: '0.85rem', background: activeTab === tab ? 'var(--surface-card)' : 'none', color: activeTab === tab ? 'var(--text-primary)' : 'var(--text-muted)', boxShadow: activeTab === tab ? 'var(--shadow-sm)' : 'none' }}>
                                {tab.charAt(0).toUpperCase() + tab.slice(1)}
                            </button>
                        ))}
                    </div>
                </div>
                <button
                    onClick={() => { fetchData(); fetchHolds(); }}
                    disabled={loading}
                    style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 16px', background: 'var(--action-primary-bg)', color: 'var(--action-primary-text)', border: 'none', borderRadius: '6px', cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.7 : 1 }}
                >
                    <FaSyncAlt size={13} /> Refresh
                </button>
            </div>

            {activeTab === 'credits' && <CreditsTab />}
            {activeTab === 'users' && <UsersTab onOpenCredits={(userId, stripeCustomerId) => setCreditsPanelUser({ userId, stripeCustomerId })} holds={holds} onRefreshHolds={fetchHolds} />}

            {activeTab === 'provisioning' && <ProvisioningTab />}

            {activeTab === 'audit' && <AuditTab />}

            {activeTab === 'pricing' && <PricingRulesTab />}

            {/* The funnel, in raw counts. Deliberately mutually exclusive except `accounts`, so they
                add up instead of overlapping into a number nobody can reconcile. */}
            {activeTab === 'billing' && summary?.funnel && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '16px' }}>
                    {[
                        // The seven states PARTITION the accounts: every one is in exactly one, so
                        // these sum to Accounts. Carded / No card / Running are orthogonal and cut
                        // across them, which is why they are separated by the divider.
                        { k: 'accounts',       label: 'Accounts',       colour: 'var(--text-primary)', hint: 'Every customer account. Internal accounts are excluded from all of these.' },
                        { k: 'paying',         label: 'Paid users',     colour: '#22c55e',             hint: 'Carded, running something, credit spent: actually being invoiced' },
                        { k: 'on_credit',      label: 'On credit',      colour: '#3b82f6',             hint: 'Carded and running something, but still on welcome credit, so not paying yet' },
                        { k: 'inactive',       label: 'Inactive',       colour: '#64748b',             hint: 'Carded but running nothing. A card is not usage.' },
                        { k: 'trial',          label: 'Trial',          colour: '#8b5cf6',             hint: 'No card, still inside its credit' },
                        { k: 'expired',        label: 'Expired',        colour: '#7f1d1d',             hint: 'No card and the credit is gone. Any resources still running here are pure loss.' },
                        { k: 'payment_failed', label: 'Payment failed', colour: '#f59e0b',             hint: 'Stripe reported a failed invoice (failed_at is set)' },
                        { k: 'suspended',      label: 'Suspended',      colour: '#ef4444',             hint: 'Suspended by an admin' },
                        { k: '', label: '', colour: '', hint: '' },
                        { k: 'carded',         label: 'Carded',         colour: 'var(--text-secondary)', hint: 'Has a card and a live subscription (cuts across the states above)' },
                        { k: 'no_card',        label: 'No card',        colour: 'var(--text-secondary)', hint: 'Never gave a card (cuts across the states above)' },
                        { k: 'running',        label: 'Running',        colour: 'var(--text-secondary)', hint: 'Has at least one live instance (cuts across the states above)' },
                        { k: 'internal',       label: 'Internal',       colour: 'var(--text-muted)',   hint: "redu's own accounts, excluded from every number above" },
                    ].map((f, i) => f.k === '' ? (
                        <div key={`sep-${i}`} style={{ width: 1, alignSelf: 'stretch', background: 'var(--border)', margin: '0 4px' }} />
                    ) : (
                        <div key={f.k} title={f.hint}
                             style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 8, padding: '8px 14px', minWidth: 96 }}>
                            <div style={{ fontSize: '1.15rem', fontWeight: 700, color: f.colour, fontVariantNumeric: 'tabular-nums' }}>
                                {(summary.funnel as any)[f.k]}
                            </div>
                            <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{f.label}</div>
                        </div>
                    ))}
                </div>
            )}

            {activeTab === 'billing' && summary && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '32px' }}>
                    {[
                        { label: 'Customers',        value: summary.total_users,
                          hint: summary.internal_users ? `+${summary.internal_users} internal, not counted` : undefined,
                          color: 'var(--status-info)' },
                        // "Month Revenue" used to be gross list-price usage with nothing subtracted,
                        // so a customer spending nothing but their £200 welcome credit counted in
                        // full. Billable is that figure minus the credit absorbing it.
                        { label: 'Billable This Month', value: `£${summary.total_month_revenue.toFixed(2)}`,
                          hint: summary.revenue_basis ? 'estimate, not collected cash' : undefined,
                          color: '#22c55e' },
                        { label: 'Covered by Credit', value: `£${(summary.total_month_credit_covered ?? 0).toFixed(2)}`,
                          hint: `£${(summary.total_month_gross_usage ?? 0).toFixed(2)} gross usage`,
                          color: '#a855f7' },
                        // Accounts with no Stripe subscription cannot be charged at all, so their
                        // usage is unrecoverable loss rather than money owed. Shown, never counted.
                        { label: 'Projected at Next Bill', value: `£${(summary.total_projected_billable ?? 0).toFixed(2)}`,
                          hint: 'if today\'s burn continues',
                          color: '#14b8a6' },
                        { label: 'Active Instances', value: summary.total_active_instances,               color: '#f59e0b' },
                        { label: 'Active Volumes',   value: summary.total_active_volumes,                 color: '#8b5cf6' },
                        { label: 'Free VMs',         value: summary.total_free_vms ?? 0,
                          hint: 'free tier, running now (V in the table)', color: PLAN_LABEL.free_tier.colour },
                        { label: 'Free Databases',   value: summary.total_free_postgres ?? 0,
                          hint: 'free tier, running now (B in the table)', color: PLAN_LABEL.free_tier.colour },
                    ].map(card => (
                        <div key={card.label} style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '10px', padding: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
                            <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{card.label}</div>
                            <div style={{ fontSize: '1.75rem', fontWeight: 700, color: card.color }}>{card.value}</div>
                            {(card as any).hint && (
                                <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: '4px' }}>{(card as any).hint}</div>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {activeTab === 'billing' && <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '10px', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', borderBottom: '1px solid var(--border)', gap: 12, flexWrap: 'wrap' }}>
                    <span style={{ fontWeight: 600, fontSize: '0.9rem', color: 'var(--text-primary)' }}>
                        Accounts ({visibleUsers.length}{stateFilter ? ` of ${users.length}` : ''}){summary?.internal_users && !showInternal ? ` · ${summary.internal_users} internal hidden` : ''}
                    </span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                        <select
                            value={stateFilter}
                            onChange={e => setStateFilter(e.target.value as '' | AccountState)}
                            style={{ padding: '5px 10px', background: 'var(--surface-input)', color: 'var(--text-primary)', border: '1px solid var(--border)', borderRadius: 6, fontSize: '0.8rem' }}
                        >
                            <option value="">All states ({countable.length})</option>
                            {(Object.keys(ACCOUNT_STATE) as AccountState[]).map(st => (
                                <option key={st} value={st}>{ACCOUNT_STATE[st].label} ({stateCounts[st] ?? 0})</option>
                            ))}
                        </select>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.8rem', color: 'var(--text-secondary)', cursor: 'pointer' }}>
                            <input type="checkbox" checked={showInternal} onChange={e => setShowInternal(e.target.checked)} />
                            Show internal
                        </label>
                    </div>
                </div>
                <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                        <thead>
                            <tr style={{ background: 'var(--bg-table)', borderBottom: '1px solid var(--border)' }}>
                                {['User', 'Plan / State', 'Project ID', 'Instances', 'Volumes', 'Snapshots', 'Backups', 'Networks', 'Month Cost', 'Billing Period Cost', 'Projected', 'Credits', 'Expires', 'Next Bill', 'Actions'].map(h => (
                                    // ⛔ Actions is PINNED to the right edge. With fifteen columns
                                    // the table scrolls horizontally and Actions sat off-screen, so
                                    // twice in a row the answer to "where is the button" was that it
                                    // existed and could not be seen.
                                    <th key={h} style={{
                                        padding: '12px 16px', textAlign: 'left', fontWeight: 600,
                                        color: 'var(--text-secondary)', whiteSpace: 'nowrap',
                                        ...(h === 'Actions' ? {
                                            position: 'sticky' as const, right: 0, zIndex: 3,
                                            background: 'var(--bg-table)',
                                            boxShadow: '-6px 0 8px -6px rgba(0,0,0,0.5)',
                                        } : {}),
                                    }}>{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {loading && users.length === 0 && (
                                <tr><td colSpan={15} style={{ padding: '32px', textAlign: 'center', color: 'var(--text-muted)' }}>Loading…</td></tr>
                            )}
                            {!loading && users.length === 0 && (
                                <tr><td colSpan={15} style={{ padding: '32px', textAlign: 'center', color: 'var(--text-muted)' }}>No accounts match this filter.</td></tr>
                            )}
                            {visibleUsers.map((u) => {
                              const st = accountState(u);
                              const meta = ACCOUNT_STATE[st];
                              const days = creditDaysLeft(u.credits_expire_at);
                              return (
                                // The state reads off the left edge before you read a single word.
                                // (The old zebra stripe was hardcoded #fff/#fafafa, which is a light
                                // theme leaking into a dark one.)
                                <tr key={u.user_id} style={{ borderBottom: '1px solid var(--border)', borderLeft: `3px solid ${meta.colour}` }}>
                                    <td style={{ padding: '12px 16px', whiteSpace: 'nowrap' }}>
                                        <span title={u.user_id} style={{ fontWeight: 500 }}>{u.username}</span>
                                        <CopyButton text={u.user_id} />
                                        {u.is_internal && (
                                            <span title="redu's own account. Excluded from every total."
                                                  style={{ marginLeft: 6, background: 'var(--surface-alt)', color: 'var(--text-muted)', border: '1px solid var(--border)', borderRadius: 999, padding: '1px 7px', fontSize: '0.68rem', fontWeight: 600 }}>internal</span>
                                        )}
                                        {holds[u.user_id] && <DeleteHeldBadge hold={holds[u.user_id]} />}

                                    </td>
                                    <td style={{ padding: '12px 16px', whiteSpace: 'nowrap' }}>
                                        <PlanPill label={u.plan_label} inputs={u.plan_label_inputs} /><FreeTierMark inputs={u.plan_label_inputs} />
                                        <span title={meta.hint} style={{ background: `${meta.colour}22`, color: meta.colour, borderRadius: 999, padding: '2px 10px', fontSize: '0.75rem', fontWeight: 700 }}>
                                            {meta.label}
                                        </span>
                                    </td>
                                    <td style={{ padding: '12px 16px', fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
                                        <span title={u.os_project}>{shortId(u.os_project)}</span>
                                        <CopyButton text={u.os_project} />
                                    </td>
                                    <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                                        <span style={{ background: u.active_instances > 0 ? 'var(--surface-selected)' : 'var(--surface-alt)', color: u.active_instances > 0 ? 'var(--text-primary)' : 'var(--text-muted)', borderRadius: '999px', padding: '2px 10px', fontWeight: 600 }}>
                                            {u.active_instances}
                                        </span>
                                    </td>
                                    <td style={{ padding: '12px 16px', textAlign: 'center' }}>{u.active_volumes}</td>
                                    <td style={{ padding: '12px 16px', textAlign: 'center' }}>{u.active_snapshots}</td>
                                    <td style={{ padding: '12px 16px', textAlign: 'center' }}>{u.active_backups}</td>
                                    <td style={{ padding: '12px 16px', textAlign: 'center' }}>{u.active_networks}</td>
                                    <td style={{ padding: '12px 16px', fontWeight: 600, color: u.month_cost > 0 ? 'var(--text-primary)' : 'var(--text-muted)', whiteSpace: 'nowrap' }}
                                        title={u.month_cost_basis === 'subscription_fee_plus_net_paid_usage'
                                            ? 'UTC calendar month: plan commitments plus paid usage beyond credits. Tax excluded; not collected cash.'
                                            : 'Recorded calendar-month usage before credits and tax.'}>
                                        {fmt(u.month_cost)}
                                    </td>
                                    <td style={{ padding: '12px 16px', whiteSpace: 'nowrap' }}
                                        title={u.subscription_period_status === 'available' && u.subscription_period
                                            ? `${u.subscription_period_basis === 'subscription_fee_plus_net_paid_usage' ? 'Commitment plus paid usage beyond credits, before tax.' : 'Recorded usage before credits and tax.'} Billing period: ${u.subscription_period.start} to ${u.subscription_period.end} (UTC).`
                                            : u.subscription_period_status === 'no_subscription'
                                                ? 'No current subscription billing period.'
                                                : 'Subscription-period usage could not be loaded. Refresh to retry.'}>
                                        {u.subscription_period_status === 'available' && u.subscription_period_cost != null && u.subscription_period ? (
                                            <>
                                                <span style={{ fontWeight: 600 }}>{fmt(u.subscription_period_cost)}</span>
                                                <small style={{ display: 'block', color: 'var(--text-muted)' }}>
                                                    {new Date(u.subscription_period.start).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' })}
                                                    {' to '}
                                                    {new Date(u.subscription_period.end).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' })}
                                                </small>
                                            </>
                                        ) : <span style={{ color: 'var(--text-muted)' }}>{u.subscription_period_status === 'no_subscription' ? 'N/A' : 'Unavailable'}</span>}
                                    </td>
                                    <td style={{ padding: '12px 16px', whiteSpace: 'nowrap' }}
                                        title={u.projection
                                            ? u.projection.basis
                                                ? `£${u.projection.accrued.toFixed(4)} already includes the commitment and credits applied, commitment credits among them. Forecast uses £${u.projection.burn.toFixed(4)}/h for ${Math.round(u.projection.hoursLeft)}h; remaining credit applies only to future usage; only a hard budget caps the forecast at its limit.`
                                                : `£${u.projection.accrued.toFixed(4)} so far + £${u.projection.burn.toFixed(4)}/h for ${Math.round(u.projection.hoursLeft)}h, less £${(u.credits_remaining ?? 0).toFixed(2)} credit`
                                            : 'No subscription, so nothing to project to'}>
                                        {u.projection && u.chargeable ? (
                                            <span style={{ fontWeight: 600, color: u.projection.afterCredit > 0 ? '#22c55e' : 'var(--text-muted)' }}>
                                                {fmt(u.projection.afterCredit)}
                                            </span>
                                        ) : <span style={{ color: 'var(--text-muted)' }}>-</span>}
                                    </td>
                                    <td style={{ padding: '12px 16px', color: u.credits_remaining > 0 ? '#15803d' : '#6b7280', fontWeight: u.credits_remaining > 0 ? 600 : 400, whiteSpace: 'nowrap' }}>
                                        {u.credits_remaining > 0 ? `£${u.credits_remaining.toFixed(4)}` : '-'}
                                    </td>
                                    <td style={{ padding: '12px 16px', whiteSpace: 'nowrap', fontSize: '0.82rem' }}>
                                        {days === null ? <span style={{ color: 'var(--text-muted)' }}>-</span> : (
                                            <span title={new Date(u.credits_expire_at as string).toLocaleString()}
                                                  style={{ color: days <= 3 ? '#ef4444' : days <= 14 ? '#f59e0b' : 'var(--text-muted)', fontWeight: days <= 14 ? 600 : 400 }}>
                                                {new Date(u.credits_expire_at as string).toLocaleDateString()}
                                                <span style={{ opacity: 0.75 }}> ({days}d)</span>
                                            </span>
                                        )}
                                    </td>
                                    <td style={{ padding: '12px 16px', whiteSpace: 'nowrap', fontSize: '0.82rem' }}
                                        title="Derived from the monthly anniversary of the day they added a card. Stripe's own period end is not stored in this database.">
                                        {u.next_bill_at ? (() => {
                                            const d = Math.ceil((new Date(u.next_bill_at as string).getTime() - Date.now()) / 86400000);
                                            return (
                                                <span style={{ color: d <= 3 ? '#22c55e' : 'var(--text-muted)', fontWeight: d <= 3 ? 600 : 400 }}>
                                                    {new Date(u.next_bill_at as string).toLocaleDateString()}
                                                    <span style={{ opacity: 0.75 }}> ({d}d)</span>
                                                </span>
                                            );
                                        })() : <span style={{ color: 'var(--text-muted)' }}>-</span>}
                                    </td>
                                    <td style={{
                                        padding: '12px 16px', position: 'sticky', right: 0, zIndex: 2,
                                        background: 'var(--bg-card)', boxShadow: '-6px 0 8px -6px rgba(0,0,0,0.5)',
                                    }}>
                                        <AccountActions
                                            userId={u.user_id}
                                            username={u.username}
                                            isInternal={!!u.is_internal}
                                            suspendedByAdmin={!!u.suspended_by_admin}
                                            onDone={() => { fetchData(); fetchHolds(); }}
                                            onCredits={() => setCreditsPanelUser({ userId: u.user_id, stripeCustomerId: u.stripe_customer_id ?? null })}
                                        />
                                    </td>
                                </tr>
                              );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>}

            {activeTab === 'billing' && <p style={{ marginTop: '12px', fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                Costs are current-month totals from <code>resource_usage</code>. User IDs are Keycloak <code>sub</code> values.
            </p>}
        </div>
    );
};

export default AdminPage;
